// Singapore, "Monsoon night on Marina Bay" (December, the north-east monsoon). From the Esplanade waterfront by the
// hawker stalls we look south-east across Marina Bay. Left to right: the Singapore Flyer turning its capsules behind a
// dripping rain tree, a far viaduct of car lights and the glass domes of the gardens, the Helix Bridge's lit double
// helix crossing the water, the glowing Supertrees behind it, the white lotus fingers of the ArtScience Museum, then
// Marina Bay Sands: three towers flaring onto their curved legs, joined at the top by the long ship of the SkyPark
// that cantilevers out past the north tower; right of it a rooftop sign (the city's billboard) and the CBD towers
// across the bay. In front: the bay mirroring everything in long coloured streaks, a bumboat crossing, a railing hung
// with bougainvillea planters, the wet promenade where an otter family trots by, a photographer shoots the skyline
// and people stroll under umbrellas, a satay stall with smoke and warm lamps, diners on plastic stools at its tables
// and an auntie at its counter. A warm tropical shower builds, pours (far lightning behind the
// towers, the SkyPark dipping into cloud) and eases off, leaving everything glistening; later a laser show.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, inEvent, hash, rng, bayer, hex,
  makeCanvas, rect, ditherFill, makeHalo, drawHalo, reflect, streak, text, textWidth, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                             // the scene's 480x270 context, set in create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const WL = 182;                                      // far shore = top row of the bay
const RAIL = 210;                                    // top rail of our waterfront railing
const QUAY = 222;                                    // our promenade starts here
const DECK = 62;                                     // SkyPark deck top row
const MBS = { x0: 212, x1: 332 };                    // Marina Bay Sands incl. the cantilever (the hero)
const TOWERS = [240, 274, 308];                      // left edge (top) of the north, middle and south towers
const TW = 20, TBOT = 182;                           // tower width at the top; they stand on the bay front
const ART = { cx: 207, y: 179 };                     // ArtScience Museum, the lotus on the water
const FLYER = { cx: 46, cy: 122, rx: 33, ry: 37 };   // Singapore Flyer (the wheel seen a little from the side)
const HELIX = { x0: 76, x1: 186, y: 177 };           // Helix Bridge deck row
const BB = { x: 344, y: 124, w: 48, h: 20 };         // face of the rooftop sign (exported as `billboard`)
const BB_BLOCK = { x0: 338, x1: 398, top: 152 };     // the waterfront block it stands on
const STALL = { x0: 394, top: 150 };                 // our hawker stall (right)
const PALM = { x: 114 };
const LAMPS = [{ x: 66 }, { x: 362 }];               // promenade lamp posts
const FOCUS = 272;

/* ================= Small pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1 */
function pick(ramp, v, i, j) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(i, j)) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only) */
function vnoise(x, y, s) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const a = hash(xi, yi, s), b = hash(xi + 1, yi, s), c = hash(xi, yi + 1, s), d = hash(xi + 1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
const toHex = (r, g, b) => '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
const mixHex = (a, b, t) => { const A = hex(a), B = hex(b); return toHex(...A.map((v, i) => Math.round(lerp(v, B[i], t)))); };
const dim = (col, k) => { const c = hex(col); return [Math.round(c[0] * k), Math.round(c[1] * k), Math.round(c[2] * k)]; };
/** crop a pix buffer to its opaque bounding box (build time); draw the result with blit() */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
const blit = o => ctx.drawImage(o.c, o.x, o.y);
/** sprite canvas from rows of characters and a colour key ('.' = transparent) */
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = pix(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
function lineP(p, x0, y0, x1, y1, col) {             // 1 px line into a pix buffer
  const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
  for (let k = 0; k <= n; k++) p.set(x0 + (x1 - x0) * k / n, y0 + (y1 - y0) * k / n, col);
}
/** a colour ramp of n steps through the given colours (cyclic) */
function ramp(cols, n) {
  const out = [];
  for (let i = 0; i < n; i++) { const f = i / n * cols.length, k = Math.floor(f); out.push(mixHex(cols[k], cols[(k + 1) % cols.length], f - k)); }
  return out;
}

/* ================= The shower: drizzle, a build-up, the downpour, easing off (loop seconds) ================= */
const POUR = [44, 164];                              // the downpour's window; it builds and eases over 30 s at each end
/** rain intensity 0..1: a warm drizzle between showers, gusting while it pours */
const pourLevel = t => env(t, POUR[0], POUR[1], 30);
const rainLevel = t => { const p = pourLevel(t); return 0.2 + 0.72 * p + 0.08 * p * (0.5 + 0.5 * wave(t, 24, 0.2)); };
const FLASHES = [{ t: 91.5, x: 300, big: false }, { t: 117.2, x: 424, big: true }];   // far lightning out over the strait
const LASER = [180, 208];                            // the SkyPark's laser show, after the shower

/* ================= Sky: a humid plum night, the city's glow warming the monsoon cloud ================= */
const SKY = ['#07060f', '#0a0814', '#0e0a1a', '#120c1f', '#170e24', '#1d1129', '#23142e', '#2a1732', '#321a36', '#3b1e39',
  '#45223c', '#50263e', '#5c2b40', '#683042', '#743644', '#7f3c46', '#8a4448'];
function renderSky() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(Math.min(1, y / 185), 1.25) * 0.66;
    v += Math.max(0, 1 - Math.hypot((i - 300) / 300, (y - 190) / 120)) * 0.42;       // the bay's light dome
    v += Math.max(0, 1 - Math.hypot((i - 60) / 110, (y - 180) / 80)) * 0.1;          // the Flyer's corner
    p.set(i, y, pick(SKY, v, i, y));
  }
  return p.done();
}

/* ================= Monsoon cloud (tileable), lit rose from below by the city ================= */
const CLOUD_DEFS = [
  { seed: 17, y: 0, h: 50, banks: 4, k: 1, rx: [16, 34], ry: [4, 7],
    pal: { top: '#1a1224', body: '#110c1a', core: '#0e0a16', bot: '#2c1a2c', bot2: '#20142a' } },
  { seed: 29, y: 30, h: 58, banks: 5, k: 2, rx: [12, 28], ry: [3.5, 6.5],
    pal: { top: '#2e1e34', body: '#1f1528', core: '#1a1122', bot: '#7a4450', bot2: '#523040' } },
];
function cloudField(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = r() * W, bw = 70 + r() * 130, base = h * (0.45 + r() * 0.3);
    const np = 6 + Math.floor(r() * 6);
    for (let i = 0; i < np; i++) {
      const rx = lerp(def.rx[0], def.rx[1], r()), ry = lerp(def.ry[0], def.ry[1], r());
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry * 1.3, rx, ry, base: base + ry * 0.3 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const q of puffs) {
      let dx = x - q.x; dx -= Math.round(dx / W) * W;
      const dy = y - q.y;
      let v = 1 - (dx * dx) / (q.rx * q.rx) - (dy * dy) / (q.ry * q.ry);
      if (y > q.base) v -= (y - q.base) * 0.5;
      if (v > best) best = v;
    }
    best += 0.16 * Math.sin(TAU * 11 * x / W + y * 0.5 + def.seed) * Math.sin(TAU * 5 * x / W - y * 0.3);
    D[y * W + x] = best;
  }
  return D;
}
/** the cloud layer, and the same shapes lit from inside by a flash (lit: a lavender-white wash) */
function makeCloudLayer(def) {
  const D = cloudField(def), h = def.h, P = def.pal;
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const p = pix(W, h), f = pix(W, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.3, v = D[y * W + x];
    if (v + th <= 0.03) continue;
    let col;
    if (get(x, y - 1) + th <= 0.03) col = P.top;
    else if (get(x, y + 1) + th <= 0.03) col = P.bot;
    else if (get(x, y + 2) <= 0.05 + bayer(x, y) * 0.2) col = P.bot2;
    else if (v > 0.55 + th) col = P.core;
    else col = P.body;
    p.set(x, y, col);
    const edge = 1 - clamp(v, 0, 1);                 // a flash lights the thin edges most
    f.set(x, y, pick(['#2a2448', '#4a3e78', '#8478b8', '#c8c0f0'], 0.2 + edge * 0.9, x, y));
  }
  return { c: p.done(), flash: f.done(), y: def.y, k: def.k };
}
/** a far lightning bolt (build time): a jagged main channel with a couple of forks */
function renderBolt(seed, len) {
  const r = rng(seed), p = pix(40, len + 2);
  let x = 20;
  const fork = (x0, y0, n, dir) => { let fx = x0; for (let y = y0; y < y0 + n && y < len; y++) { fx += dir * (r() < 0.6 ? 1 : 0) + (r() < 0.2 ? -dir : 0); p.set(fx, y, '#b8b0f0'); } };
  for (let y = 0; y < len; y++) {
    if (r() < 0.35) x += r() < 0.5 ? -1 : 1;
    if (r() < 0.08) x += r() < 0.5 ? -2 : 2;
    p.set(x, y, '#f4f0ff'); p.set(x + 1, y, '#a8a0e8');
    if (y === 14 || y === 37) fork(x, y, 16 + Math.floor(r() * 10), y === 14 ? -1 : 1);
  }
  return p.done();
}
/** the active flash: intensity 0..1 (strobes, then a fading glow), or null */
function flashAt(t) {
  for (const f of FLASHES) {
    const s = t - f.t;
    if (s < 0 || s >= 1.3) continue;
    let I = 0;
    if (s < 0.07) I = 1; else if (s < 0.16) I = 0.25; else if (s < 0.26) I = f.big ? 1 : 0.7; else if (s < 0.34) I = 0.3;
    else if (f.big && s < 0.42) I = 0.9; else I = (f.big ? 0.55 : 0.4) * Math.max(0, 1 - (s - 0.34) / 0.96);
    return { f, I, bolt: f.big && s < 0.42 && !(s >= 0.07 && s < 0.16) };
  }
  return null;
}

/* ================= Building layers: a static canvas + a canvas of lit windows toggled per floor segment ================= */
const WARM = ['#ffd49a', '#ffc47e', '#ffe2b4', '#f4b068'];
const COOL = ['#e8f0ff', '#d0e0ff', '#f4f6ff', '#c8e4f0'];
function makeLayer() { return { p: pix(W, H), lit: pix(W, H), segs: [], blinks: [] }; }
function finishLayer(L) { return { ...trim(L.p), lit: L.lit.done(), segs: L.segs, blinks: L.blinks }; }
/** floor row of windows between columns x0..x1 on row y: some baked lit or dark, the rest toggling segments */
function floorRow(L, x0, x1, y, r, o = {}) {
  const { warm = 0.55, toggle = 0.45, on = 0.5, pitch = 2, dark = '#0c0a14', min = 3, max = 9, k = 0.92 } = o;
  let x = x0;
  while (x <= x1) {
    const len = Math.min(x1 - x + 1, min + Math.floor(r() * (max - min + 1)));
    const pal = r() < warm ? WARM : COOL, col = pal[Math.floor(r() * pal.length)];
    const tg = r() < toggle, lit = !tg && r() < on;
    for (let j = 0; j < len; j += pitch) {
      L.p.set(x + j, y, lit ? dim(col, k) : dark);
      if (tg) L.lit.set(x + j, y, dim(col, Math.min(1, k + 0.08)));
    }
    if (tg) L.segs.push({ id: L.segs.length, x, y, w: len, n: 2 + Math.floor(r() * 7), p: 0.3 + r() * 0.45, off: r() });
    x += len;
  }
}

/* ================= Far shore (left): the gardens' trees and glass domes, far towns in the haze ================= */
const DOMES = [{ x0: 96, x1: 128, top: 167 }, { x0: 125, x1: 146, top: 157 }];   // Flower Dome (low, long), Cloud Forest (taller)
function renderFarShore() {
  const L = makeLayer(), p = L.p, r = rng(21);
  // far low towns beyond the bay (Marina East, the east coast): hazy blocks, a few lit windows
  for (let x = 0; x < 216;) {
    const bw = 4 + Math.floor(r() * 9), top = 158 + Math.floor(r() * 16);
    for (let y = top; y < WL; y++) for (let i = x; i < Math.min(216, x + bw); i++)
      p.set(i, y, y === top ? '#3a2a40' : i === x ? '#2e2236' : '#241a2e');
    for (let y = top + 2; y < WL - 3; y += 2) for (let i = x + 1; i < x + bw - 1; i += 2) if (r() < 0.22) p.set(i, y, r() < 0.6 ? '#a07868' : '#8a8aa8');
    x += bw + (r() < 0.3 ? 2 : 0);
  }
  // the gardens' glass domes: gridshells lit faintly from inside
  for (const [k, d] of DOMES.entries()) {
    const cx = (d.x0 + d.x1) / 2, hw = (d.x1 - d.x0) / 2, hh = WL - d.top;
    for (let x = d.x0; x <= d.x1; x++) {
      const u = (x - cx) / hw, top = Math.round(WL - hh * Math.pow(Math.max(0, 1 - u * u), k ? 0.62 : 0.45));
      for (let y = top; y < WL; y++) {
        const rib = (x - d.x0) % 4 === 0 || (y - top) % 4 === 3;
        const v = 0.3 + 0.4 * (1 - Math.abs(u)) + (y - top) / hh * 0.2;
        p.set(x, y, y === top ? (u < 0.2 ? '#8ab0b8' : '#5a7a88') : rib ? '#4a6470' : pick(['#1a2430', '#22343c', '#2e4a4e', '#3e6260'], v, x, y));
      }
    }
  }
  // the gardens' trees along the water (dark crowns) and the Flyer's terminal lawn
  for (let x = 0; x < 216; x++) {
    const top = WL - 3 - Math.round(2.5 * vnoise(x / 3.2, 0, 7) + 2 * vnoise(x / 1.4, 3, 8));
    for (let y = top; y < WL; y++) p.set(x, y, y === top ? '#1e2a26' : (hash(x, y, 3) < 0.2 ? '#16201e' : '#101816'));
  }
  return finishLayer(L);
}

/* ================= Supertrees: trunks wrapped in plants, funnel canopies lit in cycling colours ================= */
const SUPERTREES = [[144, 22], [153, 31], [163, 25], [172, 40], [183, 30], [193, 21]].map(([x, h], i) => ({ x, h, i }));
const TREE_BASE = 181;
const SKYWAY = { a: 163, b: 172, y: 160 };
const GARDEN_COLS = ['#ff5fd0', '#a070ff', '#3aa8ff', '#34e0c8', '#ffd060', '#ff85f3'];
const GRAMP = ramp(GARDEN_COLS, 48);
function renderSupertrees() {
  const p = pix(W, H), rims = [];
  for (const s of SUPERTREES) {
    const top = TREE_BASE - s.h, cw = Math.round(s.h * 0.26) + 1, ch = Math.round(s.h * 0.28), ax = s.x, ay = top + ch;
    // trunk: from the funnel's point down to a flared foot, planted panels lit dimly violet
    for (let y = ay; y <= TREE_BASE; y++) {
      const u = (y - ay) / (TREE_BASE - ay), hw = u > 0.85 ? 2 : u > 0.4 ? 1 : 0.5;
      for (let dx = -Math.floor(hw); dx <= Math.ceil(hw) - 1; dx++)
        p.set(ax + dx, y, (y * 3 + dx) % 5 === 0 ? '#7a4a9a' : (y + dx) % 4 === 0 ? '#2e5a3a' : dx < 0 ? '#3a2850' : '#2a1c3a');
    }
    // canopy seen from below: steel branches fanning up from the point to a wide ring (the ring's lights per frame)
    const nb = 9;
    for (let k = 0; k < nb; k++) {
      const u = k / (nb - 1), ex = ax - cw + u * 2 * cw, ey = top + 1 + Math.round(Math.sin(Math.PI * u) * 1.5);
      const n = Math.max(Math.abs(ex - ax), ch);
      for (let j = 1; j < n; j++) {
        const x = Math.round(lerp(ax, ex, j / n)), y = Math.round(lerp(ay, ey, Math.pow(j / n, 0.8)));
        p.set(x, y, '#2a1a3a');
        if (j % 3 === 0) rims.push({ x, y, i: s.i, fairy: true });
      }
    }
    for (let dx = -cw; dx <= cw; dx++) {                // the ring: its far edge and, a row or two lower, its near edge
      const u = (dx + cw) / (2 * cw), yb = top + 1 + Math.round(Math.sin(Math.PI * u) * 1.5);
      rims.push({ x: ax + dx, y: top, i: s.i });
      if (yb > top) rims.push({ x: ax + dx, y: yb, i: s.i, near: true });
    }
  }
  // the skyway between two of the tall ones: a thin walkway with lamps
  for (let x = SKYWAY.a + 1; x < SKYWAY.b; x++) {
    const u = (x - SKYWAY.a) / (SKYWAY.b - SKYWAY.a), y = Math.round(SKYWAY.y + Math.sin(Math.PI * u) * 1.2);
    p.set(x, y, x % 2 ? '#e8e0ff' : '#8a7aa8');
  }
  return { c: trim(p), rims };
}

/* ================= The CBD across the bay (right): towers, lit crowns, windows ================= */
const CBD_FORCED = [
  { x: 336, w: 13, top: 98, crown: 'fins' }, { x: 351, w: 15, top: 78, crown: 'fins' }, { x: 368, w: 12, top: 72, crown: 'frame' },
  { x: 383, w: 9, top: 66, crown: 'sail' }, { x: 394, w: 8, top: 76, crown: 'sail' }, { x: 404, w: 14, top: 88, crown: 'led' },
  { x: 421, w: 14, top: 64, crown: 'step' }, { x: 438, w: 12, top: 94, crown: 'slant' }, { x: 452, w: 20, top: 106, crown: 'green' },
];
const CBD_LED = { x0: 405, x1: 416, rows: [90, 92, 94] };      // a crown band of LEDs that runs through colours
const CBD_CROWNS = [];                                          // lit crown pixels (drawn with a per-frame shimmer)
function renderCbd() {
  const L = makeLayer(), p = L.p, r = rng(47);
  const list = [];
  for (let x = 334; x < W;) {
    const bw = 6 + Math.floor(r() * 10), top = 110 + Math.floor(r() * 46);
    if (!CBD_FORCED.some(f => x < f.x + f.w && x + bw > f.x)) list.push({ x, w: bw, top, crown: r() < 0.3 ? 'step' : 'flat', back: true });
    x += bw + (r() < 0.25 ? 2 : 0);
  }
  list.push(...CBD_FORCED);
  for (const b of list) {
    const topAt = i => {
      switch (b.crown) {
        case 'sail': return b.top + Math.round(Math.pow(i / (b.w - 1), 1.8) * 9);     // a curved sail edge rising to one side
        case 'slant': return b.top + Math.round(i * 0.6);
        case 'step': return b.top + (i < 2 || i >= b.w - 2 ? 4 : 0);
        case 'green': return b.top + Math.round(3 * Math.sin(i / (b.w - 1) * Math.PI * 2) + 3);
        default: return b.top;
      }
    };
    const tone = b.back ? (r() < 0.5 ? '#161222' : '#191426') : (r() < 0.5 ? '#12101e' : '#151222');
    for (let i = 0; i < b.w; i++) {
      const t0 = topAt(i);
      for (let y = t0; y < WL; y++) {
        let col = i === 0 ? '#2a2236' : i === b.w - 1 ? '#0c0a14' : tone;
        if (y === t0) col = '#34283e';
        p.set(b.x + i, y, col);
      }
    }
    const cx = b.x + (b.w >> 1);
    if (b.crown === 'fins') for (let i = 1; i < b.w - 1; i += 2) for (let y = b.top - 3; y < b.top; y++) { p.set(b.x + i, y, '#241c30'); if (y === b.top - 3) CBD_CROWNS.push([b.x + i, y, '#e8e4ff']); }
    if (b.crown === 'frame') for (let i = 0; i < b.w; i++) { CBD_CROWNS.push([b.x + i, b.top, '#ffd890']); if (i === 0 || i === b.w - 1) for (let y = b.top + 1; y < b.top + 6; y++) CBD_CROWNS.push([b.x + i, y, '#ffc070']); }
    if (b.crown === 'sail') for (let i = 0; i < b.w; i++) CBD_CROWNS.push([b.x + i, topAt(i), '#c8e0ff']);
    if (b.crown === 'slant') for (let i = 0; i < b.w; i++) CBD_CROWNS.push([b.x + i, topAt(i), '#ffb870']);
    if (b.crown === 'green') for (let y = b.top + 8; y < b.top + 30; y += 3) for (let i = 3; i < b.w - 3; i++) if (hash(i, y, 7) < 0.55) p.set(b.x + i, y, hash(i, y, 8) < 0.5 ? '#3a8a58' : '#2a6a44');
    if (b.crown === 'step' && !b.back) { for (let y = b.top - 12; y < b.top; y++) p.set(cx, y, y & 1 ? '#2a2436' : '#3a3246'); L.blinks.push([cx, b.top - 13]); }
    else if (!b.back || b.top < 124) L.blinks.push([b.x + 1, topAt(1) - 1]);
    const y0 = (b.crown === 'green' ? b.top + 32 : topAt(b.w - 1) + 3);
    for (let y = y0; y < WL - 2; y += 3) floorRow(L, b.x + 1, b.x + b.w - 2, y, r, { warm: b.back ? 0.5 : 0.35, toggle: 0.3, on: 0.4, k: b.back ? 0.7 : 0.9 });
  }
  return finishLayer(L);
}

/* ================= Singapore Flyer: the wheel's rim, cable spokes, A-frame legs, the terminal ================= */
const FLY_CAPS = 28;
const RIM_PTS = [];                                  // rim pixels in order around the wheel (for the LED chase)
{
  const seen = new Set();
  for (let k = 0; k < 720; k++) {
    const a = k / 720 * TAU, x = Math.round(FLYER.cx + Math.sin(a) * FLYER.rx), y = Math.round(FLYER.cy - Math.cos(a) * FLYER.ry);
    const key = y * W + x;
    if (!seen.has(key)) { seen.add(key); RIM_PTS.push({ x, y, u: k / 720 }); }
  }
}
function renderFlyer() {
  const p = pix(W, H), { cx, cy, rx, ry } = FLYER;
  // cable spokes: pairs crossing from the hub rings to the rim
  for (let k = 0; k < 28; k++) {
    const a = k / 28 * TAU, b = a + 0.22 * (k & 1 ? 1 : -1);
    const x0 = cx + Math.sin(b) * 3, y0 = cy - Math.cos(b) * 3, x1 = cx + Math.sin(a) * (rx - 1), y1 = cy - Math.cos(a) * (ry - 1);
    const n = Math.round(Math.max(rx, ry));
    for (let j = 2; j < n; j += 1) { const u = j / n; if (hash(k, j, 5) < 0.55) p.set(lerp(x0, x1, u), lerp(y0, y1, u), u > 0.5 ? '#2c2440' : '#221c34'); }
  }
  // the rim itself: a truss ring (dark, lit per frame)
  for (const q of RIM_PTS) p.set(q.x, q.y, '#3a3450');
  for (let k = 0; k < 720; k += 2) { const a = k / 720 * TAU; p.set(cx + Math.sin(a) * (rx - 2), cy - Math.cos(a) * (ry - 2), '#221c32'); }
  // A-frame legs from the hub down to the terminal, one pair each side of the wheel
  for (const [bx, s] of [[cx - 16, -1], [cx + 16, 1]]) {
    lineP(p, cx + s, cy, bx, 172, '#4a4460'); lineP(p, cx + s * 2, cy + 1, bx + s * 2, 172, '#2a2438');
  }
  lineP(p, cx - 10, 150, cx + 10, 150, '#34304a');
  for (let dy = -3; dy <= 3; dy++) for (let dx = -3; dx <= 3; dx++) if (dx * dx + dy * dy <= 10) p.set(cx + dx, cy + dy, dx * dx + dy * dy <= 3 ? '#8a86a8' : '#4a4660');
  // the terminal: a low curved building with lit glazing
  for (let x = cx - 34; x <= cx + 30; x++) {
    const top = 171 - Math.round(3 * Math.sin((x - cx + 34) / 64 * Math.PI));
    for (let y = top; y < WL - 2; y++) p.set(x, y, y === top ? '#8a88a8' : y === top + 1 ? '#3a3850' : (x % 3 === 0 ? '#2a2438' : pick(['#5a4a48', '#b8905c', '#f0c888'], 0.3 + (y - top) / 10, x, y)));
  }
  return trim(p);
}

/* ================= Marina Bay Sands: three towers on curved legs, the SkyPark ship across their tops ================= */
// We see the towers from the north-west: each one's west face (lit rooms) and, on its left, the north face where the
// curved east leg flares out towards the base and leans on the straight west leg (a lit atrium between them). The
// SkyPark spans all three and cantilevers out past the north tower on the left, its hull sweeping up to a thin prow.
const TOP_Y = DECK + 7;                              // first tower row under the deck
const flare = y => Math.round(8 * Math.pow(clamp((y - TOP_Y) / (TBOT - TOP_Y), 0, 1), 2));
const JOIN = 128;                                    // where the two legs meet
const deckBot = x => x >= 236 ? DECK + 6 : Math.round(DECK + 2 + 4 * Math.pow((x - MBS.x0) / 24, 1.5));
const PODIUM = { x0: 220, x1: 342, top: 169 };
const DECK_LIGHTS = [];                              // the SkyPark's side lights (per-frame shimmer)
function renderMbs(L) {
  const p = L.p, r = rng(88);
  // towers (back to front does not matter: they do not overlap)
  for (const x0 of TOWERS) {
    for (let y = TOP_Y; y < TBOT; y++) {
      const l = x0 - flare(y), rr = x0 + TW - 1;
      for (let x = l; x <= rr; x++) {
        let col;
        if (x === rr) col = '#0a0914';
        else if (x === l) col = y < JOIN ? '#3a3456' : '#5a4e70';                   // the curved leg's lit edge
        else if (x === l + 1 && y >= JOIN) col = '#2a2440';
        else if (x < x0 + 7) col = (y & 1) ? '#100e1c' : '#131020';                   // north face, in shadow
        else if (x === x0 + 7) col = '#46406a';                                        // NW corner catching the light
        else col = (y & 1) ? '#17152a' : '#1c1932';                                    // west face glass
        p.set(x, y, col);
      }
      // the atrium between the legs, lit warm
      if (y > JOIN) {
        const u = (y - JOIN) / (TBOT - JOIN), xa = x0 + 5 - Math.round(u * 7), xb = x0 + 5;
        for (let x = xa; x <= xb; x++) p.set(x, y, pick(['#3a2622', '#6a4430', '#a8703e', '#e0a860'], 0.25 + u * 0.6 - (xb - x) * 0.08, x, y));
        p.set(xa - 1, y, '#1a1628');
      }
    }
    // rooms: floor rows on the west face (toggling in segments) and a few on the north face
    for (let y = TOP_Y + 2; y < TBOT - 12; y += 2) {
      floorRow(L, x0 + 8, x0 + TW - 2, y, r, { warm: 0.72, toggle: 0.42, on: 0.55, pitch: 1, min: 2, max: 6, dark: '#12101f' });
      if (r() < 0.35) { const x = x0 + 1 + Math.floor(r() * 5); if (y < JOIN || x > x0 + 5) p.set(x, y, dim(WARM[Math.floor(r() * 4)], 0.55)); }
    }
  }
  // the SkyPark: lit balustrade on top, dark hull with a band of lights, lit underside, a thin prow
  for (let x = MBS.x0; x <= MBS.x1; x++) {
    const bot = x > MBS.x1 - 2 ? DECK + 5 : deckBot(x), top = x > MBS.x1 - 1 ? DECK + 1 : DECK;
    for (let y = top; y <= bot; y++) {
      let col = pick(['#2a2a44', '#34344e', '#444460'], 0.8 - (y - top) / 6, x, y);
      if (y === top) col = x < MBS.x0 + 3 ? '#fff4e0' : '#d8dcf0';
      else if (y === bot) col = '#8a90c0';
      else if (y === DECK + 3 && x > MBS.x0 + 4) { col = '#262640'; if (x % 3 === 0) DECK_LIGHTS.push(x); }
      p.set(x, y, col);
    }
  }
  p.set(MBS.x0, DECK - 1, '#fff4e0'); p.set(MBS.x0 + 1, DECK - 1, '#c8cce4');   // the prow's tip turns up a little
  // the garden on the deck: palms and trees in silhouette, the rim of the pool deck
  for (let x = 222; x < MBS.x1 - 2; x += 5 + Math.floor(r() * 5)) {
    const h = 2 + Math.floor(r() * 3), palm = r() < 0.5;
    for (let y = DECK - h; y < DECK; y++) p.set(x, y, '#1a1628');
    if (palm) { p.set(x - 1, DECK - h, '#1a1628'); p.set(x + 1, DECK - h, '#1a1628'); p.set(x - 2, DECK - h + 1, '#1a1628'); p.set(x + 2, DECK - h + 1, '#1a1628'); }
    else { p.set(x - 1, DECK - h + 1, '#1a1628'); p.set(x + 1, DECK - h + 1, '#1a1628'); p.set(x + 1, DECK - h, '#1e1a2e'); }
  }
  for (const x of [250, 284, 318]) { for (let y = DECK - 5; y < DECK; y++) p.set(x, y, '#2a2638'); L.blinks.push([x, DECK - 6]); }
  // the podium: the shoppes' wavy glass roof and lit halls, the steps of the event plaza down to the water
  for (let x = PODIUM.x0; x <= PODIUM.x1; x++) {
    const top = PODIUM.top + Math.round(1.5 * Math.sin((x - PODIUM.x0) / 18)) + (x < PODIUM.x0 + 6 ? 2 : 0);
    for (let y = top; y < WL; y++) {
      let col;
      if (y === top) col = '#b8b4d0';
      else if (y === top + 1) col = '#3a3450';
      else if (y >= WL - 3) col = (y === WL - 3) ? '#e8d0a8' : (x & 1 ? '#5a4a50' : '#4a3c46');              // plaza steps
      else if ((x - PODIUM.x0) % 5 === 0) col = '#2a2234';
      else col = pick(['#4a3a3a', '#8a6848', '#d8a868', '#fff0c8'], 0.35 + (y - top) / 12 * 0.5 - (hash(x >> 2, 3, 1) < 0.3 ? 0.2 : 0), x, y);
      p.set(x, y, col);
    }
  }
}

/* ================= ArtScience Museum: a white lotus of curved fingers on a bowl, in a lily pond ================= */
// [angle from vertical (deg, - = left), length, base width]: ten fingers, the nearer ones hiding two
const FINGERS = [[-68, 14, 6], [-50, 20, 6.5], [-31, 24, 7], [-12, 21, 6.5], [6, 28, 7], [24, 25, 7], [42, 21, 6.5], [62, 15, 6]];
const ART_TIPS = [];
function renderArt(wash) {
  const p = pix(W, H), { cx, y: by } = ART, body = pix(W, H);
  ART_TIPS.length = 0;
  const W1 = mixHex('#e4dcec', wash, 0.3), W2 = mixHex('#b8aacc', wash, 0.34), W3 = mixHex('#7a6a92', wash, 0.3), W4 = '#44385a';
  // the bowl: a wide shallow drum the fingers grow from
  for (let dx = -13; dx <= 13; dx++) {
    const top = by - 4 + Math.round(Math.abs(dx) / 13 * 2);
    for (let y = top; y <= by + 1; y++) body.set(cx + dx, y, y === top ? W2 : y > by - 1 ? W4 : pick([W4, W3, W2], 0.8 - Math.abs(dx) / 13 * 0.6, cx + dx, y));
  }
  // the fingers (petals), each curving outward as it rises, tapering to a lit skylight at the tip
  const order = FINGERS.map((f, i) => [Math.abs(f[0]), i]).sort((a, b) => b[0] - a[0]).map(q => q[1]);   // outer ones first (behind)
  for (const i of order) {
    const [ang, len, w0] = FINGERS[i], a1 = ang * Math.PI / 180;
    let x = cx + Math.sin(a1) * 8, y = by - 3;
    for (let s = 0; s <= len; s += 0.5) {
      const u = s / len, a = lerp(a1 * 0.45, a1 * 1.08, u), hw = lerp(w0, 2.6, Math.pow(u, 1.2)) / 2;
      x += Math.sin(a) * 0.5; y -= Math.cos(a) * 0.5;
      const nx = Math.cos(a), ny = Math.sin(a);                      // across the finger
      for (let k = -hw; k <= hw; k += 0.5) {
        const side = k / Math.max(0.5, hw), lit = ang < 0 ? -side : side;
        body.set(x + nx * k, y + ny * k, lit > 0.3 ? W1 : lit > -0.4 ? W2 : W3);
      }
    }
    ART_TIPS.push([Math.round(x), Math.round(y)]);
  }
  // outline the whole lotus in shadow so the fingers read apart
  for (let y = 140; y <= by + 1; y++) for (let x = cx - 40; x <= cx + 40; x++) {
    if (body.alpha(x, y)) { p.set(x, y, [body.d[(y * W + x) * 4], body.d[(y * W + x) * 4 + 1], body.d[(y * W + x) * 4 + 2]]); continue; }
    if (body.alpha(x - 1, y) || body.alpha(x + 1, y) || body.alpha(x, y + 1)) p.set(x, y, '#241c34');
  }
  // the lily pond around it and the promenade edge
  for (let x = cx - 22; x <= cx + 22; x++) { p.set(x, by + 2, x % 4 ? '#3a3450' : '#6a8a70'); if (!p.alpha(x, by + 1)) p.set(x, by + 1, x % 5 ? '#1e2a26' : '#4a7a4a'); }
  return trim(p);
}

/* ================= Helix Bridge: two lit steel strands spiralling round the walkway, piers into the bay ================= */
const HX_CY = HELIX.y - 6, HX_R = 6, HX_K = 0.14;
const HX_PTS = [];                                   // strand pixels, for the running lights
function renderHelix() {
  const p = pix(W, H), cyAt = x => HX_CY - Math.round(Math.sin(Math.PI * (x - HELIX.x0) / (HELIX.x1 - HELIX.x0)) * 1.5);
  // the walkway: glass canopy (faint), the deck and its warm walk lights, piers down to the bay
  for (let x = HELIX.x0; x <= HELIX.x1; x++) {
    const cy = cyAt(x), dy = HELIX.y - (HX_CY - cy);
    p.set(x, cy - 3, '#1e1c34');
    p.set(x, dy, x % 3 ? '#221e30' : '#ffd8a0'); p.set(x, dy + 1, '#2a2638');
    if (x % 22 === 10) for (let y = dy + 2; y < WL; y++) { p.set(x, y, '#2a2638'); p.set(x + 1, y, '#18162a'); }
  }
  // the two helices: the far halves dim, then the near halves bright on top; struts where they cross
  for (const front of [false, true]) for (let x = HELIX.x0; x <= HELIX.x1; x++) {
    const u = (x - HELIX.x0) / (HELIX.x1 - HELIX.x0), cy = cyAt(x);
    for (let s = 0; s < 2; s++) {
      const ph = x * HX_K + s * Math.PI, y = Math.round(cy + Math.sin(ph) * HX_R), f = Math.cos(ph) > 0;
      if (f !== front) continue;
      p.set(x, y, f ? '#c8d4f4' : '#3e4668');
      if (f) p.set(x, y + 1, '#6a78a8');
      HX_PTS.push({ x, y, u, s, front: f });
    }
    if (!front && Math.abs(Math.cos(x * HX_K)) < 0.08) for (let y = cy - HX_R + 1; y < cy + HX_R; y++) p.set(x, y, '#2e3452');
    if (!front && x % 2 === 0) { p.set(x, cy - HX_R - 1, '#3a3e5e'); p.set(x, cy + HX_R + 1, '#2a2e48'); }
  }
  return trim(p);
}

/* ================= Rooftop sign (the city's one billboard) =================
   A backlit lightbox in a steel casing ringed by a pink neon tube, on a braced trestle on the roof of a low waterfront
   block across the bay, right of Marina Bay Sands, with red aircraft lamps on its top corners. The face (BB) shows the
   city's own ad (a chilli crab on a plate, snapping its claws, steam rising, "CHILLI CRAB  SHIOK!") or, in promo
   captures, the pitch. */
const BBX = { l: BB.x - 2, r: BB.x + BB.w + 1, t: BB.y - 2, b: BB.y + BB.h + 1 };   // casing: steel ring, neon ring
const BB_POSTS = [343, 355, 367, 379, 391];
const BB_BEACONS = [[BBX.l + 1, BBX.t - 4], [BBX.r - 1, BBX.t - 4]];
const STEEL = { d: '#0a0812', m: '#1e1a2c', h: '#322c44', l: '#524a6a' };
const ACCENT = '#ff85f3';
/** the block (always there, a lit waterfront building) */
function renderBlock(L) {
  const p = L.p, r = rng(59), { x0, x1, top } = BB_BLOCK;
  for (let y = top; y < WL; y++) for (let x = x0; x <= x1; x++)
    p.set(x, y, y === top ? '#3a2e44' : x === x0 ? '#261e32' : x === x1 ? '#0c0a14' : '#171222');
  for (let y = top + 3; y < WL - 4; y += 3) floorRow(L, x0 + 1, x1 - 1, y, r, { warm: 0.6, toggle: 0.35, on: 0.45 });
  for (let x = x0; x <= x1; x++) p.set(x, WL - 3, x % 4 ? '#ffe0b0' : '#3a2e40');   // lit shopfronts along the water
}
/** the trestle, casing, lamp masts and catwalk (only with the billboard) */
function renderBillboardFrame() {
  const p = pix(W, H), { l, r: R, t: T, b: B } = BBX, T1 = BB_BLOCK.top - 1;
  for (let k = 0; k + 1 < BB_POSTS.length; k++) {
    const a = BB_POSTS[k] + 2, b = BB_POSTS[k + 1] - 1;
    lineP(p, a, B + 4, b, T1, '#2e2840'); lineP(p, a, T1, b, B + 4, '#2e2840');
  }
  for (const x of BB_POSTS) for (let y = B + 4; y <= T1; y++) { p.set(x, y, y === B + 4 ? STEEL.l : '#403858'); p.set(x + 1, y, STEEL.d); }
  for (const [x, y] of BB_BEACONS) { for (let yy = y + 1; yy < T; yy++) p.set(x, yy, STEEL.h); }
  for (let x = l; x <= R; x++) { p.set(x, T, x < l + 4 ? STEEL.l : STEEL.h); p.set(x, B, STEEL.d); }
  for (let y = T + 1; y < B; y++) { p.set(l, y, STEEL.h); p.set(R, y, STEEL.m); }
  for (let x = l - 2; x <= R + 2; x++) {
    const post = (x - l) % 4 === 1;
    p.set(x, B + 1, post ? STEEL.l : STEEL.h); p.set(x, B + 2, post ? STEEL.h : '#0a0812'); p.set(x, B + 3, x & 1 ? '#3e3654' : '#342e48');
  }
  return trim(p);
}
const CRAB = {
  open: ['c.c.......c.c', 'c.c.......c.c', '.cc.......cc.', '..c..e.e..c..', '..RRRRRRRRR..', '.RRRRRRRRRRR.', 'lRRhRRRRRhRRl',
    '.lRRRRRRRRRl.', 'l.lrrrrrrrl.l', '..l.......l..'],
  shut: ['.cc.......cc.', '.cc.......cc.', '.cc.......cc.', '..c..e.e..c..', '..RRRRRRRRR..', '.RRRRRRRRRRR.', 'lRRhRRRRRhRRl',
    '.lRRRRRRRRRl.', 'l.lrrrrrrrl.l', '..l.......l..'],
};
const CRAB_COL = { c: '#ff5a2a', e: '#1a0a0a', R: '#e8321e', r: '#a81c14', h: '#ff9a5a', l: '#c8281a' };
const CRAB_AT = { x: 3, y: 3 };
const AD_WORDS = [['CHILLI', 22, 2, '#ff7a3a'], ['CRAB', 26, 8, '#ffd84a']];
const SHIOK = { s: 'SHIOK!', x: 22, y: 14 };
function renderBillboardAd() {
  const { w, h } = BB;
  const c = ditherFill(w, h, ['#041822', '#072430', '#0b303e', '#10404e', '#16505e'], (i, j) => {
    const tubes = 0.25 * Math.exp(-(((j - 5) / 3.5) ** 2)) + 0.25 * Math.exp(-(((j - 14) / 3.5) ** 2));
    return Math.max(0, 0.45 + tubes - 0.32 * Math.max(0, 1 - Math.min(i, w - 1 - i) / 4) - 0.25 * Math.max(0, 1 - Math.min(j, h - 1 - j) / 3));
  });
  const x = c.getContext('2d');
  // the plate, red sauce pooling, a golden mantou bun
  for (let i = 1; i < 19; i++) { rect(x, i, 14, 1, 1, i < 3 || i > 16 ? '#b8b0a8' : '#f4f0e8'); rect(x, i + 1, 15, 1, 1, '#8a8480'); }
  for (let i = 3; i < 16; i++) rect(x, i, 13, 1, 1, i % 3 ? '#c82a18' : '#ff6a3a');
  rect(x, 16, 11, 3, 3, '#e8b050'); rect(x, 16, 11, 2, 1, '#ffe090'); rect(x, 18, 13, 1, 1, '#a87830');
  for (const [s, gx, gy, col] of AD_WORDS) { text(x, s, gx + 1, gy + 1, '#02080c'); text(x, s, gx, gy, col); }
  text(x, SHIOK.s, SHIOK.x + 1, SHIOK.y + 1, '#02080c');
  return c;
}

/* ================= The bay: dark plum water, the far shore's lights in long streaks ================= */
function renderWater() {
  const hh = QUAY - WL + 2, P = ['#241a30', '#1c1428', '#161020', '#110c1a', '#0c0914'];
  const p = pix(W, hh);
  for (let y = 0; y < hh; y++) for (let i = 0; i < W; i++) p.set(i, y, pick(P, Math.pow(y / hh, 0.7) * 0.999, i, y));
  return p.done();
}
/** a bumboat (river taxi): a wooden hull with a painted eye, a canopy on posts, lit cabin, lamps along the eaves */
function makeBumboat(flip) {
  const rows = [
    '....ssssssssssssssssssssssss......',
    '...sRRRRRRRRRRRRRRRRRRRRRRRRs.....',
    '....yyyyyyyyyyyyyyyyyyyyyyyy......',
    '....p..LLLLLLLLLLLLLLLLLLL.p......',
    '....pLLLLLLLLLLLLLLLLLLLLLLp......',
    '....pLLhLLLhLLLhLLLLhLLLhLLp......',
    '....pLhhhLhhhLhhhLLhhhLhhhLp......',
    'wwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwqq',
    'bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbWEWb',
    '.bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb.',
    '...dddddddddddddddddddddddddddd...',
  ];
  const key = { s: '#e8584a', R: '#a82a24', y: '#ffe4a8', p: '#4a3024', L: '#f4b464', h: '#2a1814', w: '#e8dcc0', q: '#fff0d0',
    b: '#7a2a1e', W: '#f4f0e8', E: '#101010', d: '#3a1410' };
  return sprite(rows, key, flip);
}

/* ================= Laser show from the SkyPark (soft additive beams) ================= */
const LASER_COLS = ['#5aff8a', '#48e8ff', ACCENT, '#b88aff'];
const BEAM_L = 230, BEAM_W = 7;
function makeBeam(col) {
  const [c, x] = makeCanvas(BEAM_W, BEAM_L), img = x.createImageData(BEAM_W, BEAM_L), d = img.data, rgb = hex(col), m = (BEAM_W - 1) / 2;
  for (let y = 0; y < BEAM_L; y++) {
    const along = y / (BEAM_L - 1), hw = 0.9 + (1 - along) * 2.4;
    for (let i = 0; i < BEAM_W; i++) {
      const e = Math.abs(i - m) / hw;
      if (e >= 1) continue;
      const a = Math.pow(1 - e, 1.4) * (0.25 + 0.75 * Math.pow(along, 0.6)) * 0.62, o = (y * BEAM_W + i) * 4;
      d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = Math.round(a * 255);
    }
  }
  x.putImageData(img, 0, 0);
  return c;
}
const LASER_SRC = [256, 272, 290, 306, 322];         // along the SkyPark's deck

/* ================= Our waterfront: railing with bougainvillea planters, lamp posts, the wet promenade ================= */
const PLANTERS = [[22, 30], [132, 30], [352, 30]];   // x, width (clear of the hero's reflection)
const RAIL_POSTS = [];
for (let x = 18; x < STALL.x0; x += 26) RAIL_POSTS.push(x);
const PUDDLES = [[150, 247, 20, 2], [262, 256, 26, 3], [70, 262, 16, 2]];
const BENCH = { x: 24, y: 236 };
function renderFront() {                            // the promenade; the bench and the planted bed on canvases of their own
  const p = pix(W, H), pb = pix(W, H), pd = pix(W, H), r = rng(606);
  // the promenade: wet granite slabs, darker joints, a lit kerb along the water
  for (let y = QUAY; y < H; y++) for (let x = 0; x < W; x++) {
    const d = (y - QUAY) / (H - QUAY);
    let col = pick(['#0e0a14', '#130e1a', '#181220', '#1e1628', '#261c30'], 0.62 - d * 0.5 + (hash(x >> 3, (y - QUAY) >> 2, 6) - 0.5) * 0.3, x, y);
    if ((y - QUAY) % 9 === 8 || (x + ((y - QUAY) / 9 | 0) * 11) % 24 === 0) col = '#0a0710';
    p.set(x, y, col);
  }
  for (let x = 0; x < W; x++) { p.set(x, QUAY, '#6a5a70'); p.set(x, QUAY + 1, '#3a2e44'); p.set(x, QUAY + 2, '#1a1220'); }
  // puddles mirror the plum sky
  for (const [cx, cy, rx, ry] of PUDDLES) for (let y = cy - ry; y <= cy + ry; y++) for (let x = cx - rx; x <= cx + rx; x++) {
    const d = ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2;
    if (d < 1) p.set(x, y, d > 0.7 ? '#1c1424' : pick(['#2a1c34', '#36223c', '#442a44'], 0.6 - (y - cy + ry) / (2 * ry) * 0.4, x, y));
  }
  // railing: a top rail, a mid rail, posts (the water shows between)
  for (let x = 0; x < STALL.x0 + 4; x++) { p.set(x, RAIL, '#8a8098'); p.set(x, RAIL + 1, '#3a3246'); p.set(x, RAIL + 6, '#4a4058'); }
  for (const x of RAIL_POSTS) for (let y = RAIL; y < QUAY; y++) { p.set(x, y, '#6a6078'); p.set(x + 1, y, '#241e30'); }
  // lamp posts: slim poles, a lantern head (the glow is per frame)
  for (const { x } of LAMPS) {
    for (let y = 178; y < QUAY; y++) { p.set(x, y, '#3a3448'); p.set(x + 1, y, '#16121e'); }
    p.fill(x - 2, 173, 6, 1, '#2a2438'); p.fill(x - 1, 174, 4, 3, '#ffe4b0'); p.fill(x - 1, 177, 4, 1, '#2a2438');
    p.fill(x - 1, QUAY, 4, 2, '#2a2438');
  }
  // bougainvillea planters hung on the rail: dark leaves, magenta bracts cascading over the front
  for (const [x0, w] of PLANTERS) {
    p.fill(x0, RAIL - 1, w, 7, '#3a2a2a'); p.fill(x0, RAIL - 1, w, 1, '#6a5048'); p.fill(x0, RAIL + 5, w, 1, '#1a1216');
    for (let k = 0; k < 150; k++) {
      const x = x0 - 3 + r() * (w + 6), up = r() < 0.55, y = up ? RAIL - 2 - r() * r() * 9 : RAIL + 5 + r() * r() * 11;
      if (!up && (x < x0 - 1 || x > x0 + w)) continue;
      const bract = r() < (up ? 0.5 : 0.62);
      const col = bract ? (r() < 0.25 ? ACCENT : r() < 0.6 ? '#e8409e' : '#a8206e') : (r() < 0.4 ? '#3a6a3a' : '#1e3a24');
      p.fill(Math.round(x), Math.round(y), bract && r() < 0.5 ? 2 : 1, 1, col);
    }
  }
  // a bench under the rain tree, a couple on it with their backs to us, watching the bay
  const PEOPLE = [
    [BENCH.x + 7, ['.hhh.', 'hhhhh', 'hhhhh', '.hhh.', '..n..', 'sssssss', 'sssssss', 'sssssss', 'sssssss'], { h: '#120c10', n: '#6a4a3a', s: '#3a2a4a' }, -1],
    [BENCH.x + 14, ['..hh.', '.hhhh', 'hhhhh', '.hhh.', '..n..', 'ccccccc', 'ccccccc', 'ccccccc', 'ccccccc'], { h: '#2a1a14', n: '#8a5a44', c: '#b83a5a' }, 0],
  ];
  for (const [x, rows, key, dx] of PEOPLE) rows.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (key[row[i]]) pb.set(x + i + (j < 5 ? dx + 1 : 0), BENCH.y - 12 + j, key[row[i]]); });
  for (const y of [BENCH.y - 6, BENCH.y - 3]) { pb.fill(BENCH.x, y, 30, 2, '#5a4636'); pb.fill(BENCH.x, y + 1, 30, 1, '#3a2a20'); }
  pb.fill(BENCH.x - 1, BENCH.y, 32, 1, '#6a5444'); pb.fill(BENCH.x - 1, BENCH.y + 1, 32, 1, '#2a2018');
  for (const lx of [BENCH.x + 1, BENCH.x + 27]) { pb.fill(lx, BENCH.y - 7, 2, 18, '#1a1418'); pb.set(lx, BENCH.y - 7, '#4a3a30'); }
  // a planted bed in the corner: ferns, heliconia and a bougainvillea bush behind a granite kerb
  for (let x = 0; x < 116; x++) { pd.set(x, 256, '#5a4a60'); pd.set(x, 257, '#2a2030'); for (let y = 258; y < H; y++) pd.set(x, y, pick(['#0e0a10', '#140e16', '#1a1420'], 0.4 + hash(x >> 1, y >> 1, 4) * 0.4, x, y)); }
  for (let k = 0; k < 16; k++) {                     // fern fronds arching out of the bed
    const bx = 6 + k * 7 + Math.floor(r() * 4), dir = r() < 0.5 ? -1 : 1, len = 10 + Math.floor(r() * 9);
    for (let s = 0; s < len; s++) {
      const u = s / len, x = bx + dir * s * 0.8, y = 256 - Math.sin(u * Math.PI * 0.9) * (6 + len * 0.4);
      pd.set(x, y, u < 0.5 ? '#2a5a34' : '#1e4a2a');
      if (s % 2 === 0 && u > 0.15) { pd.set(x, y + 1, '#16361e'); pd.set(x - dir, y - 1, u < 0.6 ? '#3a7a44' : '#2a5a34'); }
    }
  }
  for (const hx of [30, 72]) {                        // heliconia: red and yellow bracts on a stalk
    for (let y = 238; y < 256; y++) pd.set(hx, y, '#2a4a2a');
    for (let k = 0; k < 4; k++) { const y = 240 + k * 3, d = k % 2 ? 1 : -1; pd.set(hx + d, y, '#e83a2a'); pd.set(hx + d * 2, y + 1, '#e83a2a'); pd.set(hx + d * 3, y + 1, '#ffd040'); }
    for (const d of [-1, 1]) for (let s = 0; s < 9; s++) pd.set(hx + d * (2 + s * 0.6), 250 - s * 1.2, '#1e4a2a');
  }
  for (let k = 0; k < 160; k++) {                     // the bougainvillea bush at the right end of the bed
    const a = r() * TAU, rr = Math.sqrt(r()), x = 98 + Math.cos(a) * rr * 16, y = 248 + Math.sin(a) * rr * 8;
    if (y > 256) continue;
    const bract = r() < 0.55;
    pd.set(x, y, bract ? (r() < 0.3 ? ACCENT : r() < 0.6 ? '#e8409e' : '#a8206e') : (r() < 0.5 ? '#2a5a30' : '#16361e'));
  }
  return { front: trim(p), bench: trim(pb), bed: trim(pd) };
}

/* ================= Palms by the water (sway: three crown frames) ================= */
const PALMS = [{ x: 98, base: QUAY + 3, top: 112, lean: 6 }, { x: 128, base: QUAY + 2, top: 140, lean: -4 }];
function palmTrunk(pl) {
  const pts = [];
  for (let y = pl.base; y >= pl.top; y--) { const u = (pl.base - y) / (pl.base - pl.top); pts.push([Math.round(pl.x + pl.lean * u * u), y]); }
  return pts;
}
function renderPalmTrunks() {
  const p = pix(W, H);
  for (const pl of PALMS) for (const [x, y] of palmTrunk(pl)) {
    const ring = y % 3 === 0;
    p.set(x - 1, y, ring ? '#3a2a2a' : '#2a1e22'); p.set(x, y, ring ? '#5a4238' : '#3e2e2c'); p.set(x + 1, y, '#160e14');
    if (y > pl.base - 20) p.set(x + 2, y, '#120c12');
  }
  return trim(p);
}
function renderPalmCrown(pl, sway) {
  const p = pix(64, 44), cx = 32, cy = 12, r = rng(700 + pl.x);
  const n = 11;
  for (let k = 0; k < n; k++) {
    const a = -Math.PI * 0.95 + k / (n - 1) * Math.PI * 1.9 + (r() - 0.5) * 0.2, len = 18 + r() * 10;
    for (let s = 0; s < len; s++) {
      const u = s / len, droop = u * u * 11 + (Math.abs(Math.cos(a)) < 0.3 ? -2 : 0);
      const x = cx + Math.cos(a) * s * 1.05 + sway * u * u * 1.6, y = cy + Math.sin(a) * s * 0.55 + droop;
      p.set(x, y, u < 0.15 ? '#1a2a1e' : '#10201a');
      if (u > 0.2 && s % 2 === 0) {                    // leaflets hanging from the rachis
        const ll = Math.round(2 + (1 - u) * 3);
        for (let j = 1; j <= ll; j++) p.set(x + (Math.cos(a) > 0 ? -1 : 1) * (j > 2 ? 1 : 0), y + j, j === 1 ? '#2a4a34' : '#142a1c');
      }
      if (u > 0.1 && u < 0.7 && Math.sin(a) < 0) p.set(x, y - 1, '#3a5a44');   // a glint of the city on the upper fronds
    }
  }
  for (let dy = -2; dy <= 3; dy++) for (let dx = -2; dx <= 2; dx++) if (dx * dx + dy * dy < 7) p.set(cx + dx, cy + dy, '#1e1a14');
  p.set(cx - 1, cy + 2, '#8a6a2a'); p.set(cx + 1, cy + 3, '#6a5020');   // a few coconuts catching the lamp
  return p.done();
}

/* ================= Rain trees: the big canopy overhead, leaves lit rose from below, dripping ================= */
function renderCanopy() {
  const p = pix(W, H), r = rng(808), clumps = [];
  // the left tree: a leaning trunk up the left edge, a big bough arching over the top
  for (let y = 60; y < H; y++) {
    const cx = 6 + Math.round((H - y) * 0.03), hw = 7 + Math.round((H - y) / 60);
    for (let dx = -hw; dx <= hw; dx++) {
      const x = cx + dx, u = (dx + hw) / (2 * hw);
      if (x < 0) continue;
      let col = pick(['#4a3438', '#2e2228', '#1c141a', '#0e0a10'], 0.95 - u * 0.75 + (vnoise(x / 2, y / 6, 3) - 0.5) * 0.45, x, y);
      if (vnoise(x / 3, y / 4, 11) > 0.72 && u < 0.6) col = hash(x, y, 2) < 0.5 ? '#2a4a2a' : '#1e3a22';   // moss
      if ((y * 7 + x) % 23 === 0 && u > 0.3) col = '#0a0608';                                             // bark cracks
      p.set(x, y, col);
    }
  }
  const bough = [[8, 70], [30, 46], [70, 30], [120, 20], [170, 16], [215, 12]];
  for (let k = 0; k + 1 < bough.length; k++) {
    const [x0, y0] = bough[k], [x1, y1] = bough[k + 1], th = Math.round(6 - k * 1.1);
    for (let s = 0; s <= 1; s += 0.02) {
      const x = lerp(x0, x1, s), y = lerp(y0, y1, s);
      for (let j = -th; j <= th; j++) p.set(x, y + j, j < -th + 2 ? '#3a2a30' : j > th - 2 ? '#0a070c' : '#1a1218');
    }
  }
  // a bird's nest fern and trailing moss on the bough
  for (let k = 0; k < 16; k++) { const a = -Math.PI + k / 15 * Math.PI, len = 5 + (k % 3); for (let s = 0; s < len; s++) p.set(34 + Math.cos(a) * s, 40 + Math.sin(a) * s * 0.7 - s * 0.2, s > len - 2 ? '#4a7a3a' : '#2a5a2a'); }
  // leaf clumps: soft blobs, darker inside, lit warm-rose on their undersides by the city
  const addClump = (cx, cy, rx, ry) => clumps.push({ cx, cy, rx, ry });
  for (let x = -10; x < 250; x += 9) addClump(x + r() * 8, 4 + r() * 12 + Math.max(0, 40 - x) * 0.5, 12 + r() * 10, 6 + r() * 5);
  for (let k = 0; k < 10; k++) addClump(r() * 60, 30 + r() * 44, 8 + r() * 8, 5 + r() * 5);
  for (let x = 330; x < W + 12; x += 10) addClump(x + r() * 8, 2 + r() * 10 + Math.max(0, x - 420) * 0.25, 11 + r() * 9, 5 + r() * 5);
  const bottoms = [];
  for (const q of clumps) {
    for (let y = Math.floor(q.cy - q.ry); y <= q.cy + q.ry; y++) for (let x = Math.floor(q.cx - q.rx); x <= q.cx + q.rx; x++) {
      const dx = (x - q.cx) / q.rx, dy = (y - q.cy) / q.ry, d = dx * dx + dy * dy + (vnoise(x / 2.2, y / 2.2, 5) - 0.5) * 0.7;
      if (d >= 1 || x < 0 || x >= W || y < 0) continue;
      if (hash(x, y, 9) < 0.08 && d > 0.5) continue;                       // specks of sky between the leaves
      const low = dy > 0.35 && d > 0.45;
      p.set(x, y, low ? (hash(x, y, 4) < 0.5 ? '#4a2a34' : '#3a2230') : d < 0.3 ? '#08100c' : hash(x >> 1, y, 2) < 0.5 ? '#0e1812' : '#122018');
      if (low) bottoms.push([x, y]);
    }
  }
  return { c: trim(p), bottoms };
}

/* ================= Hawker stall (right): zinc awning, a lit sign, the satay grill; its tables and diners per frame ================= */
const AWN = { y0: STALL.top, y1: 168 };              // awning: back row, front edge
const SIGN = { x: 404, y: 156, w: 64, h: 11 };
const GRILL = { x: 402, y: 210, w: 40 };
const UNCLE = { x: 420, y: 200 };                    // his hat's top row: the grill's sticks cross his apron
const BULBS = [[412, 173], [440, 174], [466, 173]];
const SMOKERS = [0, 1, 2, 3, 4, 5, 6, 7, 8].map(k => ({ x: GRILL.x + 2 + k * 4.4, k }));
function renderStall() {
  const p = pix(W, H);
  // back wall and interior: white tiles under a fluorescent tube, shelves, a menu board
  for (let y = AWN.y1; y < 214; y++) for (let x = STALL.x0 + 2; x < W; x++) {
    const tile = (x % 5 === 0 || y % 5 === 0);
    p.set(x, y, tile ? '#9a8a74' : pick(['#a89478', '#bca88a', '#d0bc9c', '#e2d0ae'], 0.85 - (y - AWN.y1) / 46 + (x > 460 ? -0.25 : 0), x, y));
  }
  p.fill(446, 178, 30, 14, '#2a2020'); p.fill(447, 179, 28, 12, '#f4e8c8');
  for (let y = 181; y < 190; y += 2) p.fill(449, y, 10 + (y % 3) * 4, 1, '#c84a2a');
  for (let y = 181; y < 190; y += 2) p.fill(467, y, 5, 1, '#2a2020');
  p.fill(398, 196, 16, 2, '#6a5040'); for (let x = 399; x < 413; x += 3) p.fill(x, 191, 2, 5, x % 2 ? '#e84a3a' : '#f4d040');   // jars on a shelf
  // side posts and the counter: stainless steel, a glass case
  for (let y = AWN.y1; y < 240; y++) { p.set(STALL.x0 + 1, y, '#5a5468'); p.set(STALL.x0 + 2, y, '#2a2434'); }
  for (let y = 214; y < 234; y++) for (let x = STALL.x0 + 2; x < W; x++)
    p.set(x, y, y === 214 ? '#e8e4f0' : y === 215 ? '#8a86a0' : pick(['#3a3648', '#5a5670', '#8a86a0'], 0.7 - (y - 216) / 18 + (x % 7 === 0 ? -0.3 : 0), x, y));
  // the grill: a long charcoal trough on legs in front of the counter (coals and sticks per frame)
  p.fill(GRILL.x - 1, GRILL.y + 2, GRILL.w + 2, 3, '#2a2226'); p.fill(GRILL.x - 1, GRILL.y + 5, GRILL.w + 2, 1, '#120c10');
  for (const lx of [GRILL.x + 1, GRILL.x + GRILL.w - 2]) p.fill(lx, GRILL.y + 6, 1, 18, '#1a1418');
  // the awning: corrugated zinc sloping to a front edge, the sign fixed to its fascia
  for (let x = STALL.x0 - 4; x < W; x++) for (let y = AWN.y0; y <= AWN.y1; y++) {
    const u = (y - AWN.y0) / (AWN.y1 - AWN.y0);
    if (x < STALL.x0 - 4 + Math.round((1 - u) * 6)) continue;
    const rib = (x + Math.round(u * 3)) % 3 === 0;
    p.set(x, y, y === AWN.y1 ? '#1a1620' : rib ? '#6a6478' : pick(['#2a2634', '#3a3446', '#4a4458'], 0.3 + u * 0.5, x, y));
  }
  // the sign: a yellow lightbox, red letters
  for (let y = SIGN.y; y < SIGN.y + SIGN.h; y++) for (let x = SIGN.x; x < SIGN.x + SIGN.w; x++) {
    const edge = y === SIGN.y || x === SIGN.x || y === SIGN.y + SIGN.h - 1 || x === SIGN.x + SIGN.w - 1;
    p.set(x, y, edge ? '#6a3a1a' : pick(['#e8a830', '#ffd048', '#fff080'], 0.75 - Math.abs(y - SIGN.y - 5) / 8, x, y));
  }
  return trim(p);
}
const SIGN_TXT = [['SATAY', 409, 159], ['BBQ', 434, 159]];
const UNCLE_ROWS = ['..www..', '.wwwww.', '.hffhh.', '..ff...', '.ttttt.', 'tttttta', 'ttttt.a', 'ttttt..', '.ttttt.', '.aaaaa.'];
const UNCLE_KEY = { w: '#f4f0e8', h: '#1a1210', f: '#b87a54', t: '#e8e4ec', a: '#3a2a4a' };

/* ================= Otters: a family trotting along the promenade (two gait frames) ================= */
const OTTER = {
  big: [['.......BBBB.......', '....BBbbbbbbB..HH.', '..tbbbbbbbbbbbhhhn', 'ttbbbbbbbbbbbbccc.', '....cccbbbbbbll...', '....ll......l.l...'],
    ['......BBBBB.......', '....Bbbbbbbbb..HH.', '..tbbbbbbbbbbbhhhn', 'ttbbbbbbbbbbbbccc.', '....cccbbbbbbl....', '...l..l....l..l...']],
  small: [['.....BBB.....', '...BBbbbB.HH.', '.tbbbbbbbbhhn', 'tbbbbbbbbbcc.', '...ll...l.l..'],
    ['....BBBB.....', '...Bbbbbb.HH.', '.tbbbbbbbbhhn', 'tbbbbbbbbbcc.', '..l.l..l..l..']],
};
const OTTER_COL = { B: '#a07e60', H: '#8a6a50', h: '#5e4230', n: '#0a0606', b: '#5a3e2c', c: '#c0a282', t: '#48301f', l: '#1e140e' };
const OTTERS = [0, 1, 2, 3, 4].map(i => ({ i, big: i === 0, t0: 34 + i * 2.1 + (i > 2 ? 0.8 : 0), dive: 298 - i * 4 }));
const OTTER_Y = QUAY + 9;                            // their paws on the promenade by the rail
const OTTER_V = 9.5;                                 // px/s

/* ================= People: diners on plastic stools, the hawkers, a photographer and a couple at the rail ================= */
// One function draws everyone (after rome.js): a look gives the proportions for a height and its colours dimmed towards
// the night, and the hair and shoulders catch a row of the nearest light (the stall's lamps, or the bay's glow).
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
const SKINS = ['#f0c8a0', '#d8a47c', '#b07a52', '#7e5236'], HAIRS = ['#141012', '#2e1c16', '#4a2e1e', '#8a8490'];
const NIGHT = '#170e22', STALL_LIT = '#ffd8a8', BAY_LIT = '#f0b8c8';
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.22, lit = o.lit || STALL_LIT, m = c => mixHex(c, NIGHT, dk);
  const hh = h >= 21 ? 5 : 4, th = Math.round(h * 0.36);
  return { hh, hw: 4, th, tw: h >= 21 ? 7 : 6, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], lit, 0.3), top: m(top), topD: mixHex(top, NIGHT, dk + 0.3),
    topL: mixHex(top, lit, 0.35), bot: m(bot), botL: m(mixHex(bot, lit, 0.3)), shoe: m('#1e1820'), long: o.long || 0, skirt: o.skirt ? m(o.skirt) : null,
    apron: o.apron ? m(o.apron) : null, bun: !!o.bun };
}
/**
 * One person centred on column x, feet on row fy. o.view: 0 from behind, 1 facing us, 2 in profile (o.dir 1 right, -1 left)
 * o.step: stride frame (profile); o.sit: on a stool (thighs on its seat at fy - 4, shins down to fy); o.waist: from the
 * waist up (behind a counter); o.lean: head px over
 * Arms (o.arm in profile, the near one; o.aL / o.aR otherwise): 0 down, 'table' a forearm on a table (its top at fy - 9),
 * 'mouth' a hand to the mouth, 'drink' a glass (o.glass) to the mouth, 'talk', 'film' a phone held out, 'photo' a phone
 * raised beside the head (from behind, its lit screen shows), 'brolly' an elbow out holding an umbrella, 'wipe' a hand on
 * the counter with a cloth (o.wipe: its offset)
 */
function person(x, fy, P, o = {}) {
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const bw = side ? tw - 2 : tw, tx = x - (bw >> 1), ty = o.sit ? fy - 5 - th : fy - P.ll - th + 1, hy = ty - hh;
  const F = (dx, y, w, h, c) => R(dir > 0 ? x + dx : x - dx - w + 1, y, w, h, c);   // dx px forwards of x
  if (o.waist) { /* behind a counter: no legs */ }
  else if (o.sit && side) { F(-1, fy - 6, 7, 1, P.botL); F(-1, fy - 5, 7, 1, P.bot); F(4, fy - 4, 2, 4, P.bot); F(4, fy, 3, 1, P.shoe); }
  else if (o.sit) { R(tx, fy - 6, bw, 1, P.botL); R(tx, fy - 5, bw, 1, P.bot); R(tx, fy - 4, 2, 4, P.bot); R(tx + bw - 2, fy - 4, 2, 4, P.bot); R(tx, fy, 2, 1, P.shoe); R(tx + bw - 2, fy, 2, 1, P.shoe); }
  else {
    const ly = fy - P.ll + 1, legC = P.skirt ? P.skin : P.bot;
    if (side && o.step) { F(-2, ly, 4, 2, P.bot); F(1, ly + 2, 2, P.ll - 2, legC); F(1, fy, 3, 1, P.shoe); F(-2, ly + 2, 2, P.ll - 3, legC); F(-2, fy - 1, 2, 1, P.shoe); }
    else if (side) { F(-1, ly, 2, P.ll, legC); F(-1, fy, 3, 1, P.shoe); }
    else {
      const lw = bw >= 7 ? 3 : 2;
      R(tx, ly, bw, 2, P.bot); R(tx, ly + 2, lw, P.ll - 2, legC); R(tx + bw - lw, ly + 2, lw, P.ll - 2, legC);
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
    }
    if (P.skirt) { if (side) F(-2, ly, 5, (P.ll >> 1) + 1, P.skirt); else R(tx, ly, bw, (P.ll >> 1) + 1, P.skirt); }
  }
  R(tx, ty, bw, th, P.top);
  if (side) F(-2, ty + 1, 1, th - 1, P.topD); else R(tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  if (P.apron && view === 1) R(tx + 1, ty + 2, bw - 2, th - 2, P.apron);
  R(tx, ty, bw, 1, P.topL);                                                      // the light on the shoulders
  const sk = P.skin, d = P.topD;
  if (side) {
    const a = o.arm || 0;
    if (!a) { const ax = x + (o.step ? -dir : 0); R(ax, ty + 1, 1, th - 2, d); R(ax, ty + th - 1, 1, 1, sk); }
    else if (a === 'table') { R(x, ty + 1, 1, fy - 11 - ty, d); F(0, fy - 10, 4, 1, d); F(4, fy - 10, 1, 1, sk); }
    else if (a === 'mouth' || a === 'drink') {
      R(x, ty + 1, 1, 2, d); F(1, ty, 1, 2, d); F(2, ty - 2, 1, 2, sk);
      if (a === 'drink') F(3, ty - 4, 1, 3, o.glass); else F(3, ty - 3, 1, 1, o.utensil || '#d8c090');
    }
    else if (a === 'talk') { R(x, ty + 1, 1, 2, d); F(1, ty + 2, 1, 1, d); F(2, ty + 1, 1, 1, d); F(3, ty, 1, 1, sk); }
    else if (a === 'film') { R(x, ty + 1, 1, 2, d); F(1, ty + 2, 3, 1, d); F(4, ty + 1, 1, 2, sk); F(5, ty - 1, 1, 4, '#121018'); F(4, ty - 1, 1, 1, '#a8c0f0'); }
    else if (a === 'brolly') { R(x, ty + 1, 1, 2, d); F(1, ty + 1, 1, 1, d); F(2, ty, 1, 1, sk); }   // the shaft at x + 2 dir
  } else {
    const arm = (ax, a, sg) => {
      if (!a) { R(ax, ty + 1, 1, th - 2, d); R(ax, ty + th - 1, 1, 1, sk); }
      else if (a === 'photo') {
        R(ax, hy + 1, 1, ty - hy, d); R(ax, hy, 1, 1, sk);
        const px = sg < 0 ? ax - 2 : ax;                                         // the screen: the towers' lights over the bay
        R(px, hy - 2, 3, 1, '#c8c0f0'); R(px, hy - 1, 3, 1, '#ffc890'); R(px + 1, hy - 2, 1, 1, '#fff0d0');
      }
      else if (a === 'mouth') { R(ax, ty, 1, 2, d); R(ax - sg, hy + hh - 2, 1, 2, sk); }
      else if (a === 'drink') { R(ax, ty, 1, 2, d); R(ax - sg, hy + hh - 1, 1, 2, sk); R(x - 1, hy + hh - 2, 2, 3, o.glass); }
      else if (a === 'brolly') R(ax, ty + 1, 1, 2, d);
      else if (a === 'wipe') { R(ax, ty + 1, 1, th - 2, d); R(ax + o.wipe, ty + th - 2, 1, 1, sk); R(ax + o.wipe - 1, ty + th - 1, 3, 1, '#d8dce4'); }
    };
    arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1);
  }
  const lean = o.lean || 0, hr = hh >= 5 ? 2 : 1;
  const hx = side ? (dir > 0 ? x - 2 + lean : x - 1 - lean) : x - (hw >> 1) + lean;
  if (view === 0) { R(hx, hy, hw, hh, P.hair); if (P.long) R(hx, hy + hh, hw, P.long, P.hair); else R(hx + 1, hy + hh - 1, hw - 2, 1, sk); }
  else {
    R(hx, hy, hw, hh, sk); R(hx, hy, hw, hr, P.hair);
    if (side) R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair);
    else if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); }
  }
  R(hx, hy, hw, 1, P.hairL);
  if (P.bun) R(hx + 1, hy - 1, 2, 1, P.hair);
  return hy;
}
const STOOL_COLS = [['#f07a6a', '#c83a3a', '#7a2228'], ['#6aa0f0', '#3a6ac8', '#22407a'], ['#6ad8a0', '#2a9a6a', '#1a5a40']]
  .map(s => s.map(c => mixHex(c, NIGHT, 0.18)));
/** a plastic stool under someone seated at x (view and dir as theirs): the seat at fy - 4, the legs splayed */
function stool(x, fy, k, view, dir) {
  const [l, m, d] = STOOL_COLS[k], w = view === 1 ? 7 : 6, x0 = view === 1 || dir > 0 ? x - 3 : x - 2;
  R(x0, fy - 4, w, 1, l); R(x0, fy - 3, w, 1, m); R(x0, fy - 2, 1, 2, d); R(x0 + w - 1, fy - 2, 1, 2, d); R(x0 - 1, fy, 1, 1, d); R(x0 + w, fy, 1, 1, d);
}
/** a round marble-topped hawker table on a pedestal, its top on row fy - 9 */
function table(x0, x1, fy) {
  const cx = (x0 + x1) >> 1;
  R(x0, fy - 9, x1 - x0 + 1, 1, '#e4dee8'); R(x0 + 1, fy - 8, x1 - x0 - 1, 1, '#8a8498');
  R(cx, fy - 7, 1, 7, '#5a5668'); R(cx + 1, fy - 7, 1, 7, '#3a3648'); R(cx - 2, fy, 6, 1, '#3a3648');
}
/** an open umbrella, its crown on row y: canopy, its shade, the crown catching the light */
function brolly(cx, y, [m, d, l]) {
  R(cx, y - 1, 1, 1, d); R(cx - 4, y, 9, 1, l); R(cx - 6, y + 1, 13, 1, m); R(cx - 7, y + 2, 15, 1, m);
  for (const k of [-7, -3, 1, 5]) R(cx + k, y + 3, 2, 1, d);
}
/** the smeared mirror image of someone standing on the wet granite */
function wetMirror(x, fy, w, P) {
  ctx.globalAlpha = 0.18; R(x - (w >> 1), fy + 1, w, 2, P.bot);
  ctx.globalAlpha = 0.1; R(x - (w >> 1), fy + 3, w, 3, P.top);
  ctx.globalAlpha = 1;
}

/* ================= The warm tropical shower at three depths, lit by the bay's lights ================= */
const RAIN_LIGHTS = [
  { x: 286, y: 118, r: 54, c: [255, 214, 160] }, { x: ART.cx, y: 162, r: 26, c: [240, 200, 255] },
  { x: 168, y: 158, r: 28, c: [255, 130, 230] }, { x: FLYER.cx, y: FLYER.cy, r: 44, c: [210, 220, 255] },
  { x: 425, y: 92, r: 30, c: [210, 225, 255] }, { x: 440, y: 190, r: 56, c: [255, 196, 120] },
  { x: LAMPS[0].x, y: 176, r: 28, c: [255, 214, 150] }, { x: LAMPS[1].x, y: 176, r: 28, c: [255, 214, 150] },
  { x: BB.x + (BB.w >> 1), y: BB.y + (BB.h >> 1), r: 34, c: [255, 150, 240], bb: true },   // the rooftop sign's neon
].map(l => ({ ...l, r2: l.r * l.r }));
// a picked weather (site/js/engine/weather.js): Singapore draws its own rain, so a Rain pick keeps this shower; snow and
// leaves land on the far shore, the bay and past the bottom of the frame, and catch the same lights
const WEATHER = weatherPlans(['rain']);
const WX_LAND = [[132, WL], [WL + 2, QUAY - 2], [H + 4, H + 4]];
const wxLight = l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r, col: toHex(...l.c) });
const WX = { land: WX_LAND, lights: RAIN_LIGHTS.filter(l => !l.bb).map(wxLight) };
const WX_BB = { land: WX_LAND, lights: RAIN_LIGHTS.map(wxLight) };
function litAt(x, y, board) {
  for (let i = 0; i < RAIN_LIGHTS.length; i++) {
    const l = RAIN_LIGHTS[i], dx = x - l.x, dy = y - l.y;
    if ((board || !l.bb) && dx * dx + dy * dy < l.r2) return i;
  }
  return -1;
}
function nearLand(x, a, b) {
  if (x >= STALL.x0 - 2 && a < 0.45) return AWN.y0 + Math.round((AWN.y1 - AWN.y0) * a / 0.45);   // drums on the zinc awning
  if (a < 0.12) return RAIL;
  return QUAY + 3 + Math.floor(b * (H - QUAY - 4));
}
const RAIN = [
  { count: 380, nMin: 110, nMax: 130, fall: 200, ff: 1, len: 3, slope: 0.1, a: 0.26, land: (x, a) => 96 + Math.floor(a * (WL - 96)) },
  { count: 250, nMin: 150, nMax: 172, fall: 250, ff: 0.9, len: 5, slope: 0.15, a: 0.34, water: true, land: (x, a) => WL + 2 + Math.floor(a * (QUAY - WL - 4)) },
  { count: 136, nMin: 196, nMax: 216, fall: 320, ff: 0.86, len: 9, slope: 0.22, a: 0.44, land: nearLand },
];
RAIN.forEach((L, li) => {
  const r = rng(100 + li);
  L.col = `rgba(226,200,220,${L.a})`;
  L.splash = li === 2 ? '#e8d4e4' : '#b8a0bc';
  L.lit = RAIN_LIGHTS.map(l => `rgba(${l.c[0]},${l.c[1]},${l.c[2]},${Math.min(0.9, L.a + 0.3)})`);
  L.drops = [];
  for (let i = 0; i < L.count; i++) L.drops.push({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r() });
});
const RINGS = [], PUDDLE_RINGS = [];
{ const r = rng(71); for (let i = 0; i < 90; i++) RINGS.push({ id: i, n: 100 + Math.floor(r() * 40), ph: r() }); }
{ const r = rng(72); for (let i = 0; i < 16; i++) PUDDLE_RINGS.push({ id: 60 + i, n: 80 + Math.floor(r() * 30), ph: r(), p: i % PUDDLES.length }); }
// drips from the rain trees' lowest leaves (onto the promenade, the bench, the awning) and off the awning's edge
let DRIPS = [];
const EAVES = [];
for (let x = STALL.x0 - 2; x < W; x += 3) EAVES.push({ x, id: x, n: 150 + (x * 7) % 40, ph: hash(x, 5, 5) });
/* the rain veils of the downpour, drifting across the far shore */
function renderVeil() {
  const h = WL - 20, p = pix(W, h);
  for (let x = 0; x < W; x++) {
    const col = 0.5 + 0.5 * Math.sin(TAU * 3 * x / W + 1.3) * Math.sin(TAU * 7 * x / W), streaky = hash(x, 1, 3);
    for (let y = 0; y < h; y++) {
      const v = col * 0.7 + streaky * 0.3 - (1 - y / h) * 0.35 + (vnoise(x / 1.5, y / 14, 9) - 0.5) * 0.4;
      if (v > bayer(x, y) * 0.9 + 0.2) p.set(x, y, y > h - 30 ? '#a88498' : '#8a7090', Math.round(Math.min(1, v) * 150));
    }
  }
  return p.done();
}
/* low cloud dragging across the SkyPark while it pours (density breathing, levels crossfaded) */
const FOG_DEFS = [
  { seed: 5, y: 38, h: 46, k: 1, dir: 1, top: '#3a2a40', bot: '#5a3a4c', alpha: 0.7, dens: t => 0.08 + 0.72 * pourLevel(t) + 0.08 * wave(t, 5, 0.2) },
  { seed: 9, y: 150, h: 44, k: 2, dir: -1, top: '#4a3448', bot: '#5e4458', alpha: 0.5, dens: t => 0.2 + 0.5 * pourLevel(t) + 0.1 * wave(t, 4, 0.6) },
];
const FOG_LEVELS = 8;
function makeFog(def) {
  const h = def.h, n = W * h, F = new Float32Array(n), A = hex(def.top), B = hex(def.bot);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const v = y / (h - 1), prof = Math.pow(Math.sin(Math.PI * Math.pow(v, 0.8)), 1.6);
    const nz = 0.48 + 0.34 * Math.sin(TAU * 2 * x / W + def.seed + y * 0.05) + 0.16 * Math.sin(TAU * 5 * x / W - y * 0.19 + def.seed * 2)
      + 0.1 * Math.sin(TAU * 11 * x / W + y * 0.41) + 0.06 * Math.sin(TAU * 23 * x / W - y * 0.7);
    F[y * W + x] = prof * clamp(nz, 0, 1);
  }
  const levels = [];
  for (let k = 0; k <= FOG_LEVELS; k++) {
    const D = k / FOG_LEVELS * 1.25, [c, cx] = makeCanvas(W, h), img = cx.createImageData(W, h), d = img.data;
    for (let q = 0; q < n; q++) {
      const x = q % W, y = (q / W) | 0, v = Math.floor(Math.min(1, F[q] * D) * 4 + bayer(x, y)) / 4;
      if (v <= 0) continue;
      const m = y / (h - 1), o = q * 4;
      d[o] = A[0] + (B[0] - A[0]) * m; d[o + 1] = A[1] + (B[1] - A[1]) * m; d[o + 2] = A[2] + (B[2] - A[2]) * m;
      d[o + 3] = Math.round(Math.min(1, v) * 255);
    }
    cx.putImageData(img, 0, 0);
    levels.push(c);
  }
  return { ...def, levels };
}

/* ================= Timed events (loop seconds) ================= */
const BOATS = [
  { a: 14, b: 104, x0: -40, x1: 492, wl: 199 },     // a bumboat crosses left to right in the far lane
  { a: 126, b: 214, x0: 492, x1: -40, wl: 205 },    // and back in the nearer one
];
const PLANES = [{ a: 22, b: 74, x0: 492, x1: 120, y0: 44, y1: 30 }, { a: 140, b: 198, x0: -12, x1: 360, y0: 24, y1: 36 }];
const WALKER = { a: 148, b: 206, x0: 404, x1: -24, y0: QUAY + 11, y1: 252 };   // round the stall, crossing towards us
const TUBE_FLICK = [[27, 28.6], [133, 134.2], [219, 220.4]];
const BB_FLICK = [[58.2, 59.6], [161.5, 162.4], [228, 229.6]];
const blinkI = t => { const f = frac(t / 1.5); return f < 0.1 ? f / 0.1 : Math.max(0, 1 - (f - 0.1) / 0.45); };   // 160 per loop
const OTTER_PAUSE = [52, 55.5];                      // the whole family stops together to look around
const otterPaused = (o, t) => Math.max(0, Math.min(t, OTTER_PAUSE[1]) - Math.max(o.t0, OTTER_PAUSE[0]));
const otterX = (o, t) => -20 + OTTER_V * (t - o.t0 - otterPaused(o, t));
const otterDive = o => o.t0 + (o.dive + 20) / OTTER_V + (OTTER_PAUSE[1] - OTTER_PAUSE[0]);   // when it reaches the rail and slips under it
// People. The diners eat, talk and drink on their own beats all loop, the auntie behind the counter wipes it down and a
// man waits for his satay. At the rail a photographer shoots the skyline under his umbrella; at 47 s he turns to film the
// otters coming along the rail and at 63 s follows them to their dive, then shoots the skyline again (and the laser show).
// At 84 s a couple under one umbrella comes round the stall, stops at the rail to look at the towers (110-130 s) and
// strolls on, behind the bench and the rain tree, out of the frame by 168 s.
const SNAP = { x: 188, fy: QUAY + 4, otters: [47, 63, 80] };
const STROLL = { a: 84, stop: 110, go: 130, b: 168, x0: 404, xs: 236, x1: -22, fy: QUAY + 11 };
const SNAP_P = look(19, 2, 0, '#3a4a6a', '#2a2a34', { dk: 0.36, lit: BAY_LIT });
const WALKER_P = [21, 22, 23, 24].map(h => look(h, 1, 0, '#2a5a6a', '#8a7a6a', { dk: 0.3, lit: BAY_LIT, long: 3 }));
const PAIR = [look(22, 1, 1, '#c8c0b0', '#2a2a3a', { dk: 0.36, lit: BAY_LIT }),
  look(20, 0, 2, '#a84a7a', '#3a2a3a', { dk: 0.36, lit: BAY_LIT, long: 3, skirt: '#a84a7a' })];
const BROLLY = { snap: ['#24345a', '#161e36', '#5a6aa0'], pair: ['#e0a83a', '#9a6a1c', '#ffe08a'], walk: ['#c83a8a', '#7a1e52', '#ff85f3'] };
const TABLES = [[408, 422, 249], [444, 462, 258]];   // x0, x1, floor row
const DINERS = [                                     // stool: STOOL_COLS index
  { x: 405, fy: 249, view: 2, dir: 1, stool: 0, P: look(22, 2, 0, '#e8e4dc', '#2a3048') },
  { x: 425, fy: 249, view: 2, dir: -1, stool: 1, P: look(21, 0, 1, '#3aa0a0', '#1e1a22', { long: 2 }) },
  { x: 458, fy: 255, view: 1, dir: 1, stool: 0, P: look(21, 1, 2, '#e8a0c0', '#3a3a58', { long: 3 }) },
  { x: 441, fy: 258, view: 2, dir: 1, stool: 2, P: look(22, 3, 0, '#e89a3a', '#2a2a3a') },
];
const HAWKER = { x: 466, fy: 222, P: look(20, 1, 3, '#c84a5a', '#2a2430', { dk: 0.12, apron: '#e8e4dc', bun: true }) };
const QUEUER = { x: 472, fy: 238, P: look(22, 1, 0, '#f0c840', '#1e1a22', { dk: 0.2 }) };
const DINER_POSES = [                                // each diner's poses by name; drawDiners picks one
  { table: { arm: 'table' }, mouth: { arm: 'mouth', utensil: '#e8d8a8' } },
  { table: { arm: 'table' }, table1: { arm: 'table', lean: 1 }, talk: { arm: 'talk' }, talk1: { arm: 'talk', lean: 1 }, drink: { arm: 'drink', glass: '#c08048' } },
  { rest: {}, mouth: { aR: 'mouth' }, drink: { aR: 'drink', glass: '#8ac850' } },
  { table: { arm: 'table' }, mouth: { arm: 'mouth', utensil: '#9a4a22' } },
];
const STREAKS = [
  { x: 250, w: 8, col: '#ffd8a0', a: 0.36, seed: 1 }, { x: 284, w: 8, col: '#ffe4b8', a: 0.36, seed: 2 }, { x: 318, w: 8, col: '#ffd098', a: 0.34, seed: 3 },
  { x: 280, w: 30, col: '#e8b878', a: 0.2, seed: 4 }, { x: ART.cx, w: 12, col: '#f0dcff', a: 0.3, seed: 5 },
  { x: FLYER.cx, w: 10, col: '#d8e0ff', a: 0.22, seed: 6 }, { x: 368, w: 10, col: '#e8e8ff', a: 0.2, seed: 7 },
  { x: 428, w: 8, col: '#c8e0ff', a: 0.26, seed: 8 }, { x: 132, w: 8, col: '#8ad8d0', a: 0.16, seed: 9 },
];

/* ================= Pre-render everything static ================= */
const ART_WASHES = ['#ffffff', '#ff9ae8', '#b8a0ff', '#a0e8ff'];
const FLY_COLS = ['#e8f0ff', '#ff9ae8', '#c090ff', '#80c8ff', '#9affe0', '#ffd0f0'];
const FLY_RAMP = ramp(FLY_COLS, 24);
let HL, SKY_C, CLOUDS, FAR, TREES, CBD, FLYER_C, FLY_RIM_C, HERO, ART_C, HELIX_C, BBF_C, BB_AD, CRAB_C, SHIOK_C, PITCH,
  WATER_C, BOAT_C, BEAM_C, FRONT_C, BENCH_C, BED_C, TRUNK_C, CROWN_C, CANOPY, STALL_C, UNCLE_C, OTTER_C, PUFF_C, FOGS, VEIL_C,
  VIG_C, BOLT_C, SIGN_TXT_C, GLINTS, WALKER_SPR, SNAP_SPR, PAIR_SPR, DINE_SPR, TABLE_SPR, HAWKER_SPR, QUEUER_SPR;
function prerender() {
  HL = {
    flash: makeHalo(120, '#8a7ad8', 0.3, 1.3), flashCore: makeHalo(44, '#d0c8ff', 0.34, 1.4),
    mbs: makeHalo(84, '#ffc890', 0.12, 1.6), skypark: makeHalo(34, '#e0e4ff', 0.2, 1.5), atrium: makeHalo(9, '#ffb060', 0.34),
    podium: makeHalo(40, '#ffc070', 0.16, 1.5), art: makeHalo(34, '#ecd8ff', 0.2, 1.5), tip: makeHalo(4, '#fff8ff', 0.5, 1),
    tree: GARDEN_COLS.map(c => makeHalo(12, c, 0.34)), garden: makeHalo(40, '#c060ff', 0.12, 1.5),
    helix: makeHalo(46, '#aab8ff', 0.1, 1.6), flyer: makeHalo(52, '#b8c8ff', 0.11, 1.5), hub: makeHalo(6, '#e0e8ff', 0.4),
    cbd: makeHalo(64, '#ffc8a0', 0.09, 1.5), crown: makeHalo(9, '#ffe0b0', 0.26), led: makeHalo(12, '#80f0ff', 0.3),
    red: makeHalo(5, '#ff3030', 0.6, 1), strobe: makeHalo(4, '#f0f0ff', 0.6, 1), green: makeHalo(4, '#40ff80', 0.5, 1),
    bbGlow: makeHalo(42, ACCENT, 0.12, 1.5), bbTube: makeHalo(6, ACCENT, 0.32), boat: makeHalo(24, '#ffc070', 0.2), boatLamp: makeHalo(5, '#ffe0a0', 0.4),
    lamp: makeHalo(28, '#ffc878', 0.26), lampCore: makeHalo(8, '#fff0d0', 0.42), stall: makeHalo(64, '#ffb060', 0.16, 1.5),
    bulb: makeHalo(12, '#ffd890', 0.42), coal: makeHalo(16, '#ff6a20', 0.34), tube: makeHalo(30, '#e8fff8', 0.18), sign: makeHalo(30, '#ffd048', 0.2),
    laser: LASER_COLS.map(c => makeHalo(8, c, 0.5, 1.3)), hit: LASER_COLS.map(c => makeHalo(14, c, 0.34, 1.4)),
  };
  SKY_C = renderSky(); CLOUDS = CLOUD_DEFS.map(makeCloudLayer); BOLT_C = renderBolt(31, 110);
  FAR = renderFarShore(); TREES = renderSupertrees(); CBD = renderCbdLayer();
  FLYER_C = renderFlyer();
  { // the rim in each of its colours, on a canvas just the wheel's size
    const x0 = FLYER.cx - FLYER.rx - 1, y0 = FLYER.cy - FLYER.ry - 1, w = FLYER.rx * 2 + 3, h = FLYER.ry * 2 + 3;
    FLY_RIM_C = FLY_RAMP.map(col => { const p = pix(w, h); for (const q of RIM_PTS) p.set(q.x - x0, q.y - y0, col); return { c: p.done(), x: x0, y: y0 }; });
  }
  { const L = makeLayer(); renderMbs(L); HERO = finishLayer(L); }
  ART_C = ART_WASHES.map(w => renderArt(w));
  HELIX_C = renderHelix();
  BBF_C = renderBillboardFrame(); BB_AD = renderBillboardAd();
  CRAB_C = { open: sprite(CRAB.open, CRAB_COL), shut: sprite(CRAB.shut, CRAB_COL) };
  SHIOK_C = [ACCENT, '#ffe0fa'].map(col => { const [c, x] = makeCanvas(textWidth(SHIOK.s), 5); text(x, SHIOK.s, 0, 0, col); return c; });
  PITCH = makePitch(BB.w, BB.h, { accent: ACCENT });
  WATER_C = renderWater(); BOAT_C = [makeBumboat(false), makeBumboat(true)];
  BEAM_C = LASER_COLS.map(makeBeam);
  ({ front: FRONT_C, bench: BENCH_C, bed: BED_C } = renderFront()); TRUNK_C = renderPalmTrunks();
  CROWN_C = PALMS.map(pl => [-1, 0, 1].map(s => renderPalmCrown(pl, s)));
  CANOPY = renderCanopy();
  { // drips from the lowest leaves: onto the bench, the promenade, the awning
    const r = rng(909), cand = CANOPY.bottoms.filter(([x, y]) => (x < 64 && y > 26) || (x > 150 && x < 250 && y > 14) || (x > 336 && y > 10));
    DRIPS = [];
    for (let k = 0; k < 22 && cand.length; k++) {
      const [x, y] = cand[Math.floor(r() * cand.length)];
      if (DRIPS.some(d => Math.abs(d.x - x) < 3)) continue;
      const y1 = x >= STALL.x0 - 4 ? AWN.y0 + 3 + Math.floor(r() * 10) : x >= BENCH.x && x < BENCH.x + 30 && r() < 0.5 ? BENCH.y - 1 : QUAY + 6 + Math.floor(r() * 36);
      DRIPS.push({ x, y0: y + 1, y1, n: 40 + Math.floor(r() * 50), ph: r(), id: k });
    }
  }
  { // wet things that glint once the shower eases: the rail, the planters, leaf tips, the awning's edge, the bench
    const r = rng(313);
    GLINTS = [];
    for (let x = 2; x < STALL.x0; x += 4 + Math.floor(r() * 5)) GLINTS.push([x, RAIL]);
    for (const [x0, w] of PLANTERS) for (let k = 0; k < 5; k++) GLINTS.push([x0 + Math.floor(r() * w), RAIL - 2 - Math.floor(r() * 5)]);
    for (let k = 0; k < 24; k++) { const b = CANOPY.bottoms[Math.floor(r() * CANOPY.bottoms.length)]; GLINTS.push([b[0], b[1] + 1]); }
    for (let x = STALL.x0; x < W; x += 6) GLINTS.push([x, AWN.y1]);
    for (let k = 0; k < 4; k++) GLINTS.push([BENCH.x + 2 + Math.floor(r() * 26), BENCH.y]);
  }
  STALL_C = renderStall(); UNCLE_C = sprite(UNCLE_ROWS, UNCLE_KEY);
  SIGN_TXT_C = (() => {
    const [c, x] = makeCanvas(SIGN.w, SIGN.h);
    for (const [s, sx, sy] of SIGN_TXT) text(x, s, sx - SIGN.x, sy - SIGN.y, '#c81e14');
    for (let k = 0; k < 3; k++) { rect(x, 50 + k * 3, 2, 1, 7, '#8a5a2a'); rect(x, 50 + k * 3 - 1, 3, 3, 3, '#a8401a'); rect(x, 50 + k * 3, 4, 1, 1, '#e87a3a'); }   // three satay sticks
    return c;
  })();
  OTTER_C = {};
  for (const k of ['big', 'small']) OTTER_C[k] = OTTER[k].map(rows => sprite(rows, OTTER_COL));
  PUFF_C = [];
  for (let pr = 1; pr <= 4; pr++) PUFF_C[pr] = [['#ffe0c0', '#f0c8a8'], ['#d8c4c4', '#b8a8b4']].map(([lo, hi]) => {
    const s = pr * 2 + 1, p = pix(s, s);
    for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) { const d = Math.hypot(i - pr, y - pr) / pr; if (d < 1 && (1 - d) * 1.7 > bayer(i, y) * 0.9) p.set(i, y, y > pr ? lo : hi); }
    return p.done();
  });
  FOGS = FOG_DEFS.map(makeFog); VEIL_C = renderVeil();
  VIG_C = renderVignette();
  { // the people in each of their poses (their boxes hold the umbrellas, the reflections and the drinks)
    const W0 = WALKER.x0, Y0 = WALKER.y0, S = SNAP, X = STROLL.xs, F = STROLL.fy;
    WALKER_SPR = WALKER_P.map(P => [0, 1].map(st => bake(W0 - 12, Y0 - 32, 21, 39, () => paintWalker(P, !!st))));
    SNAP_SPR = Object.fromEntries(['photo', 'down', -1, 1].map(k => [k, bake(S.x - 10, S.fy - 28, 21, 35, () => paintSnapper(k))]));
    PAIR_SPR = Object.fromEntries(['stop', 0, 1].map(k => [k, bake(X - 12, F - 30, 25, 37, () => paintPair(k))]));
    DINE_SPR = DINERS.map((D, i) => Object.fromEntries(Object.entries(DINER_POSES[i]).map(([k, o]) => [k, bake(D.x - 10, D.fy - 22, 21, 23, () => paintDiner(D, o))])));
    TABLE_SPR = TABLES.map(([x0, x1, fy], k) => bake(x0 - 2, fy - 13, x1 - x0 + 5, 14, () => paintTable(k)));
    const H0 = { view: 1, waist: true };
    HAWKER_SPR = Object.fromEntries([['rest', {}], ...[-2, -1, 0, 1, 2].map(w => [w, { aR: 'wipe', wipe: w }])]
      .map(([k, o]) => [k, bake(HAWKER.x - 8, 200, 17, 14, () => person(HAWKER.x, HAWKER.fy, HAWKER.P, { ...H0, ...o }))]));
    QUEUER_SPR = [0, -1].map(lean => bake(QUEUER.x - 6, 214, 13, 25, () => person(QUEUER.x, QUEUER.fy, QUEUER.P, { view: 0, lean })));
  }
}
function renderCbdLayer() {                           // the CBD towers, then the low waterfront block the sign stands on
  const L = makeLayer();
  renderBlock(L);
  return { towers: renderCbd(), block: finishLayer(L) };
}
function renderVignette() {
  const p = pix(W, H), c = [4, 3, 8];
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.72, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, c, Math.round(q * 0.45 * 255));
  }
  return p.done();
}

/* ================= Per-frame: sky, lightning, clouds, planes ================= */
function drawScroll(cv, y, k, t, dir = 1) {
  let off = Math.floor(frac(k * t / LOOP) * W);
  if (dir < 0) off = (W - off) % W;
  ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y);
}
function drawFog(f, t) {
  const D = clamp(f.dens(t), 0, 0.999) * FOG_LEVELS, k = Math.floor(D), fr = D - k;
  for (const [cv, a] of [[f.levels[k], 1 - fr], [f.levels[k + 1], fr]]) {
    if (a * f.alpha < 0.004) continue;
    ctx.globalAlpha = a * f.alpha;
    drawScroll(cv, f.y, f.k, t, f.dir);
  }
  ctx.globalAlpha = 1;
}
function drawPlane(pl, t) {
  if (t < pl.a || t >= pl.b) return;
  const s = (t - pl.a) / (pl.b - pl.a), x = Math.round(lerp(pl.x0, pl.x1, s)), y = Math.round(lerp(pl.y0, pl.y1, s)), dir = pl.x1 > pl.x0 ? 1 : -1;
  rect(ctx, x - 3, y, 7, 1, '#1a1424'); rect(ctx, x - dir * 3, y - 1, 1, 1, '#1a1424');
  rect(ctx, x + dir * 3, y, 1, 1, '#fff0d8');                                     // landing light, on its nose
  halo(HL.strobe, x + dir * 3, y, 0.5);
  if (frac(t / 1.2) < 0.5) { rect(ctx, x - dir, y + 1, 1, 1, '#ff3a3a'); halo(HL.red, x - dir, y + 1, 0.6); }
  if (frac(t / 1.6 + 0.3) < 0.08) { rect(ctx, x - dir * 3, y - 1, 1, 1, '#ffffff'); halo(HL.strobe, x - dir * 3, y - 1, 0.9); }
}
function drawSky(t, fl, pour) {
  ctx.drawImage(SKY_C, 0, 0);
  if (fl) {
    halo(HL.flash, fl.f.x, 50, fl.I * 0.9); halo(HL.flashCore, fl.f.x, 44, fl.I * 0.8);
    ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = 0.2 * fl.I; rect(ctx, 0, 0, W, WL, '#5a4c8a');
    ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  }
  drawScroll(CLOUDS[0].c, CLOUDS[0].y, CLOUDS[0].k, t);
  if (fl) { ctx.globalAlpha = fl.I * 0.6; drawScroll(CLOUDS[0].flash, CLOUDS[0].y, CLOUDS[0].k, t); ctx.globalAlpha = 1; }
  for (const pl of PLANES) drawPlane(pl, t);
  if (fl && fl.bolt) { ctx.globalAlpha = Math.min(1, fl.I); ctx.drawImage(BOLT_C, fl.f.x - 20, 56); ctx.globalAlpha = 1; }
  drawScroll(CLOUDS[1].c, CLOUDS[1].y, CLOUDS[1].k, t);
  if (fl) { ctx.globalAlpha = fl.I * 0.75; drawScroll(CLOUDS[1].flash, CLOUDS[1].y, CLOUDS[1].k, t); ctx.globalAlpha = 1; }
  if (pour > 0.01) { ctx.globalAlpha = 0.22 * pour; rect(ctx, 0, 0, W, WL, '#0a0610'); ctx.globalAlpha = 1; }   // the shower's cloud deck
}

/* ================= Per-frame: the far shore, the Flyer, the gardens, the CBD ================= */
function drawLayer(L, t, salt) {
  blit(L);
  let rx = 0, ry = 0, rw = 0;                          // runs of adjacent lit segments are copied in one go
  for (const s of L.segs) {
    if (hash(s.id + salt, Math.floor(frac(t / LOOP + s.off) * s.n), 4) >= s.p) continue;
    if (rw && s.y === ry && s.x === rx + rw) { rw += s.w; continue; }
    if (rw) ctx.drawImage(L.lit, rx, ry, rw, 1, rx, ry, rw, 1);
    rx = s.x; ry = s.y; rw = s.w;
  }
  if (rw) ctx.drawImage(L.lit, rx, ry, rw, 1, rx, ry, rw, 1);
}
const flyCol = t => Math.floor(frac(t * 5 / LOOP) * FLY_RAMP.length) % FLY_RAMP.length;
function drawFlyer(t) {
  halo(HL.flyer, FLYER.cx, FLYER.cy, 0.9);
  blit(FLYER_C);
  blit(FLY_RIM_C[flyCol(t)]);
  // a chase of white light running round the rim
  const ph = frac(t * 12 / LOOP);
  ctx.fillStyle = '#ffffff';
  for (const q of RIM_PTS) { const d = frac(q.u - ph); if (d < 0.05 || (d > 0.5 && d < 0.55)) ctx.fillRect(q.x, q.y, 1, 1); }
  // the capsules, turning three places per loop (so the wheel lines up with itself at the seam)
  const rot = 3 * t / LOOP;
  for (let k = 0; k < FLY_CAPS; k++) {
    const a = (k + rot) / FLY_CAPS * TAU, x = Math.round(FLYER.cx + Math.sin(a) * (FLYER.rx + 2)) - 1, y = Math.round(FLYER.cy - Math.cos(a) * (FLYER.ry + 2)) - 1;
    rect(ctx, x, y, 3, 1, k % 7 === 3 ? '#ffd8a0' : '#dff0ff'); rect(ctx, x, y + 1, 3, 1, '#4a5070');
  }
  halo(HL.hub, FLYER.cx, FLYER.cy, 0.7 + 0.2 * wave(t, 16));
}
function drawGardens(t) {
  halo(HL.garden, 170, 166, 0.8 + 0.2 * wave(t, 6));
  blit(TREES.c);
  // Supertree canopies: each ring runs through the garden colours; now and then the whole grove pulses in step
  const sync = env(frac(t / 60) * 60, 30, 44, 3);    // 4 times a loop the grove lights in one colour, pulsing
  const gi = Math.floor(frac(t * 8 / LOOP) * GRAMP.length);
  const tw = step(t, 720);
  for (const q of TREES.rims) {
    const own = (gi + q.i * 7 + (q.near ? 2 : 0)) % GRAMP.length, pulse = 0.5 + 0.5 * wave(t, 120, q.i * 0.13);
    if (q.fairy && hash(q.x, q.y, tw) < 0.3) continue;   // lights along the branches twinkle
    ctx.globalAlpha = q.fairy ? 0.7 : 1;
    ctx.fillStyle = sync > 0.5 ? (pulse > 0.4 ? GRAMP[(gi + 20) % GRAMP.length] : '#3a2448') : GRAMP[own];
    ctx.fillRect(q.x, q.y, 1, 1);
  }
  ctx.globalAlpha = 1;
  for (const s of SUPERTREES) halo(HL.tree[(Math.floor(gi / 8) + s.i) % GARDEN_COLS.length], s.x, TREE_BASE - s.h + 2, 0.8);
}
function drawCbd(t) {
  halo(HL.cbd, 410, 120, 0.9);
  drawLayer(CBD.towers, t, 11);
  ctx.fillStyle = '#ffe4b8';
  const s = step(t, 480);
  for (const [x, y, col] of CBD_CROWNS) { if (hash(x, y, s) < 0.06) continue; ctx.fillStyle = col; ctx.fillRect(x, y, 1, 1); }
  halo(HL.crown, 374, 74, 0.8); halo(HL.crown, 387, 68, 0.6);
  // the crown band of LEDs: colours running along it
  const ph = frac(t * 16 / LOOP);
  CBD_LED.rows.forEach((y, j) => { for (let x = CBD_LED.x0; x <= CBD_LED.x1; x++) { ctx.fillStyle = GRAMP[Math.floor(frac(ph + (x - CBD_LED.x0) / 24 + j * 0.1) * GRAMP.length) % GRAMP.length]; ctx.fillRect(x, y, 1, 1); } });
  halo(HL.led, 411, 92, 0.7);
  drawLayer(CBD.block, t, 61);
}
function drawBlinkers(t) {
  const I = blinkI(t);
  if (I <= 0.02) return;
  ctx.fillStyle = I > 0.5 ? '#ff4a3a' : '#b02020';
  for (const [x, y] of [...HERO.blinks, ...CBD.towers.blinks]) { ctx.fillRect(x, y, 1, 1); halo(HL.red, x, y, I); }
}

/* ================= Per-frame: Marina Bay Sands, the lotus, the Helix ================= */
function drawMbs(t) {
  halo(HL.mbs, 286, 112, 0.9 + 0.08 * wave(t, 8));
  halo(HL.skypark, 272, 64, 0.8);
  drawLayer(HERO, t, 23);
  // the SkyPark's side lights shimmer; the prow's lamp at the tip of the cantilever
  const s = step(t, 960);
  ctx.fillStyle = '#fff2d8';
  for (const x of DECK_LIGHTS) if (hash(x, s, 3) > 0.12) ctx.fillRect(x, DECK + 3, 1, 1);
  rect(ctx, MBS.x0, DECK, 2, 1, '#ffffff'); halo(HL.tip, MBS.x0 + 1, DECK, 0.8 + 0.2 * wave(t, 30));
  for (const x0 of TOWERS) halo(HL.atrium, x0 + 3, 160, 0.7);
  halo(HL.podium, 282, 176, 0.85);
}
function drawArt(t) {
  const v = frac(t * 3 / LOOP) * ART_WASHES.length, k = Math.floor(v), f = smooth(0.7, 1, v - k);   // holds, then fades to the next wash
  halo(HL.art, ART.cx, 164, 0.9);
  blit(ART_C[k % ART_C.length]);
  if (f > 0.01) { ctx.globalAlpha = f; blit(ART_C[(k + 1) % ART_C.length]); ctx.globalAlpha = 1; }
  const sp = step(t, 240);
  ctx.fillStyle = '#fff8ff';
  ART_TIPS.forEach(([x, y], i) => { ctx.fillRect(x, y, 1, 1); if (hash(i, sp, 7) < 0.5) halo(HL.tip, x, y, 0.5); });
}
function drawHelix(t) {
  halo(HL.helix, 132, 170, 0.9);
  blit(HELIX_C);
  // running lights: pulses chase along each strand in opposite directions; red and green base-pair lamps blink in turn
  const ph = frac(t * 20 / LOOP), s = step(t, 480);
  for (const q of HX_PTS) {
    const d = frac((q.s ? -q.u : q.u) * 3 - ph);
    if (d < 0.12) { ctx.fillStyle = q.front ? '#ffffff' : '#b8c4f0'; ctx.fillRect(q.x, q.y, 1, 1); }
    else if (q.front && q.x % 11 === 0) { ctx.fillStyle = (q.x + s) % 2 ? '#ff4a4a' : '#4aff7a'; ctx.fillRect(q.x, q.y, 1, 1); }
  }
}

/* ================= Per-frame: the rooftop sign ================= */
function signLevel(t, ev, salt) {
  if (!inEvent(t, ev)) return 1;
  const hv = hash(salt, Math.floor(t * 24), 3);
  return hv > 0.55 ? 1 : hv > 0.3 ? 0.4 : 0;
}
const bbNeon = t => signLevel(t, BB_FLICK, 45) * (0.93 + 0.07 * wave(t, 2400, 0.4));
function drawBillboardAd(t) {
  ctx.drawImage(BB_AD, BB.x, BB.y);
  const snap = frac(t / 2.4) < 0.18 || (frac(t / 2.4) > 0.3 && frac(t / 2.4) < 0.4);   // the claws snap twice every 2.4 s
  ctx.drawImage(snap ? CRAB_C.shut : CRAB_C.open, BB.x + CRAB_AT.x, BB.y + CRAB_AT.y);
  // steam off the plate
  ctx.fillStyle = '#e8f4f8';
  for (let k = 0; k < 3; k++) {
    const ph = k * 0.33, ks = 40 + k * 7;
    for (let j = 1; j <= 4; j++) {
      if (frac(ks * t / LOOP + ph - j * 0.23) < 0.25) continue;
      const sw = Math.round(Math.sin(j * 0.9 - TAU * (ks * t / LOOP + ph)) * Math.min(1.5, j * 0.5));
      ctx.globalAlpha = 0.8 * (1 - (j - 1) / 4);
      const y = BB.y + 3 - j + (k === 1 ? 0 : 1);
      if (y >= BB.y) ctx.fillRect(BB.x + 5 + k * 4 + sw, y, 1, 1);
    }
  }
  ctx.globalAlpha = 1;
  const u = frac(t * 40 / LOOP);                       // SHIOK! pops on, holds, blinks twice (every 6 s)
  const on = u < 0.75 ? u > 0.12 : (u > 0.8 && u < 0.85) || u > 0.9;
  if (on) ctx.drawImage(SHIOK_C[u < 0.2 ? 1 : 0], BB.x + SHIOK.x, BB.y + SHIOK.y);
}
function drawBillboard(t, pitch) {
  const lv = bbNeon(t), { l, r: R, t: T, b: B } = BBX;
  halo(HL.bbGlow, BB.x + (BB.w >> 1), BB.y + (BB.h >> 1), lv);
  blit(BBF_C);
  if (!PITCH.covers(t, pitch)) drawBillboardAd(t);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  ctx.fillStyle = lv > 0.7 ? ACCENT : lv > 0.2 ? '#a0509a' : '#402040';
  ctx.fillRect(l + 1, T + 1, R - l - 1, 1); ctx.fillRect(l + 1, B - 1, R - l - 1, 1);
  ctx.fillRect(l + 1, T + 2, 1, B - T - 3); ctx.fillRect(R - 1, T + 2, 1, B - T - 3);
  for (const [x, y] of [[l + 1, T + 1], [R - 1, T + 1], [l + 1, B - 1], [R - 1, B - 1]]) halo(HL.bbTube, x, y, lv * 0.8);
  const I = blinkI(t);
  if (I > 0.02) { ctx.fillStyle = I > 0.5 ? '#ff4a3a' : '#b02020'; for (const [x, y] of BB_BEACONS) { ctx.fillRect(x, y, 1, 1); halo(HL.red, x, y, I); } }
}

/* ================= Per-frame: lasers over the bay ================= */
const laserLevel = t => env(t, LASER[0], LASER[1], 1.5);
function drawLasers(t, lv) {
  if (lv <= 0.01) return;
  const cs = step(t, 120), fan = 0.5 + 0.5 * wave(t, 40);   // the fan opens and closes every 6 s
  ctx.save();
  ctx.globalCompositeOperation = 'lighter'; ctx.imageSmoothingEnabled = true;
  LASER_SRC.forEach((x, i) => {
    const c = (i + cs) % LASER_COLS.length;
    for (const sgn of [-1, 1]) {
      const ang = sgn * (0.12 + fan * (0.25 + i * 0.12)) + 0.18 * wave(t, 60 + i * 4, i * 0.2), co = Math.cos(ang), si = Math.sin(ang);
      ctx.globalAlpha = lv * (0.75 + 0.25 * wave(t, 300 + i * 17, i * 0.3));
      ctx.setTransform(co, si, -si, co, x, DECK - 1);
      ctx.drawImage(BEAM_C[c], -(BEAM_W - 1) / 2, -BEAM_L);
    }
  });
  ctx.restore();
  LASER_SRC.forEach((x, i) => halo(HL.laser[(i + cs) % LASER_COLS.length], x, DECK - 1, lv * 0.9));
  // where the beams touch the cloud base, soft spots
  LASER_SRC.forEach((x, i) => {
    for (const sgn of [-1, 1]) {
      const ang = sgn * (0.12 + fan * (0.25 + i * 0.12)) + 0.18 * wave(t, 60 + i * 4, i * 0.2), hy = 34;
      halo(HL.hit[(i + cs) % LASER_COLS.length], x + Math.tan(ang) * (DECK - 1 - hy), hy, lv * 0.5);
    }
  });
}

/* ================= Per-frame: the bay ================= */
function drawRings(t, level) {
  ctx.fillStyle = '#c8b4d4';
  const n = Math.round(RINGS.length * level);
  for (let i = 0; i < n; i++) {
    const g = RINGS[i], q = t * g.n / LOOP + g.ph, fq = Math.floor(q), cyc = fq % g.n, u = q - fq;
    if (u > 0.45) continue;
    const s = u / 0.45, x = hash(g.id, cyc, 1) * W, y = WL + 3 + hash(g.id, cyc, 2) * (QUAY - WL - 5);
    const rr = 1 + s * (2 + (y - WL) / 12), ry = rr * 0.32, pts = 6 + Math.floor(rr * 2.5);
    ctx.globalAlpha = (1 - s) * 0.6;
    for (let j = 0; j < pts; j++) { const a = j / pts * TAU; ctx.fillRect(Math.round(x + Math.cos(a) * rr), Math.round(y + Math.sin(a) * ry), 1, 1); }
  }
  // the downpour pits the whole surface: tiny splashes everywhere, a new scatter 20 times a second
  const ps = step(t, 4800), np = Math.round(170 * level * level);
  ctx.globalAlpha = 0.55;
  for (let k = 0; k < np; k++) ctx.fillRect(Math.floor(hash(k, ps, 51) * W), WL + 2 + Math.floor(hash(k, ps, 52) * (QUAY - WL - 3)), 1, 1);
  ctx.globalAlpha = 1;
}
/** splash crowns all over the wet promenade while it pours */
function drawGroundPits(t, level) {
  const ps = step(t, 4800), np = Math.round(70 * level * level);
  ctx.fillStyle = '#d8c4dc';
  for (let k = 0; k < np; k++) {
    const x = Math.floor(hash(k, ps, 61) * STALL.x0), y = QUAY + 4 + Math.floor(hash(k, ps, 62) * (H - QUAY - 5)), sp = 1 + Math.floor(hash(k, ps, 63) * 2.4);
    ctx.globalAlpha = 0.3 + 0.3 * hash(k, ps, 64);
    ctx.fillRect(x - sp, y, 1, 1); ctx.fillRect(x + sp, y, 1, 1);
  }
  ctx.globalAlpha = 1;
}
function drawWater(t, board, rain, level, lv) {
  ctx.drawImage(WATER_C, 0, WL);
  reflect(ctx, t, { top: WL, rows: QUAY - WL, alpha: 0.62, k1: 40, k2: 96, squash: 1.7 });
  rect(ctx, 0, WL, W, 1, '#120c1a');
  for (const s of STREAKS) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, WL + 1, QUAY);
  streak(ctx, t, 168, 14, GRAMP[Math.floor(frac(t * 8 / LOOP) * GRAMP.length) % GRAMP.length], 0.3, 12, WL + 1, QUAY);   // the Supertrees
  streak(ctx, t, FLYER.cx + 18, 5, FLY_RAMP[flyCol(t)], 0.22, 13, WL + 1, QUAY);
  if (board) streak(ctx, t, BB.x + (BB.w >> 1), 10, '#ff9af0', 0.2 * bbNeon(t), 14, WL + 1, QUAY);   // the sign's pink neon
  if (lv > 0.01) LASER_SRC.forEach((x, i) => streak(ctx, t, x, 3, LASER_COLS[(i + step(t, 120)) % LASER_COLS.length], 0.3 * lv, 20 + i, WL + 1, QUAY));
  const gs = step(t, 1440);                           // glints on the chop
  for (let k = 0; k < 40; k++) {
    if (hash(k, gs, 17) < 0.4) continue;
    const x = Math.floor(hash(k, gs, 18) * W), y = WL + 2 + Math.floor(hash(k, gs, 19) * (QUAY - WL - 4));
    const warm = STREAKS.some(s => Math.abs(s.x - x) < s.w + 3);
    ctx.globalAlpha = warm ? 0.75 : 0.35; ctx.fillStyle = warm ? '#ffe8c8' : '#9a8ab8';
    ctx.fillRect(x, y, warm ? 2 : 1, 1);
  }
  ctx.globalAlpha = 1;
  if (rain) drawRings(t, level);
}
function boatReflect(spr, x, wl, rows, a, t) {
  const w = spr.width, h = spr.height;
  rows = Math.min(rows, QUAY - wl - 1, h);
  for (let j = 0; j < rows; j++) {
    const off = Math.round(Math.sin(TAU * 40 * t / LOOP + j * 0.7) * (0.5 + j * 0.15));
    ctx.globalAlpha = a * (1 - j / rows);
    ctx.drawImage(spr, 0, h - 1 - j, w, 1, x + off, wl + 1 + j, w, 1);
  }
  ctx.globalAlpha = 1;
}
function drawWake(x, w, wl, dir, len, t) {
  const stern = dir > 0 ? x + 2 : x + w - 3, bow = dir > 0 ? x + w - 1 : x, s = step(t, 1200);
  ctx.fillStyle = '#e8e0f0';
  for (let d = 1; d < len; d++) {
    const xx = stern - dir * d;
    if (xx < 0 || xx >= W) continue;
    const fade = 1 - d / len, yn = wl + 1 + Math.round(d * 0.08);
    if (yn < QUAY && hash(d, s, 41) < 0.7) { ctx.globalAlpha = 0.6 * fade; ctx.fillRect(xx, yn, 1, 1); }
    if (d < len * 0.6 && hash(d, s, 43) < 0.9 * fade) { ctx.globalAlpha = 0.8 * fade; ctx.fillRect(xx, wl + (hash(d, s, 44) < 0.5 ? 0 : 1), 1, 1); }
  }
  ctx.globalAlpha = 0.85;
  for (let k = 0; k < 6; k++) if (hash(k, s, 45) < 0.8) ctx.fillRect(bow + dir * (k >> 1), wl - (k & 1), 1, 1);
  ctx.globalAlpha = 1;
}
function drawBoat(b, t) {
  if (t < b.a || t >= b.b) return;
  const dir = b.x1 > b.x0 ? 1 : -1, spr = BOAT_C[dir > 0 ? 0 : 1], w = spr.width, h = spr.height;
  const x = Math.round(lerp(b.x0, b.x1, (t - b.a) / (b.b - b.a))), wl = b.wl + Math.round(wave(t, 50, b.a / 100) * 0.6);
  boatReflect(spr, x, wl, 8, 0.36, t);
  drawWake(x, w, wl, dir, 46, t);
  ctx.drawImage(spr, x, wl - h + 1);
  streak(ctx, t, x + (w >> 1), 10, '#ffc878', 0.34, 31, wl + 1, QUAY);
  halo(HL.boat, x + (w >> 1), wl - 5, 0.75);
  for (let k = 0; k < 5; k++) halo(HL.boatLamp, x + (dir > 0 ? 4 : 5) + k * 5, wl - h + 2, 0.5 + 0.2 * wave(t, 200 + k * 13));
  if (frac(t / 1.6) < 0.5) rect(ctx, dir > 0 ? x + 3 : x + w - 4, wl - h, 1, 1, '#ff4a4a');
}

/* ================= Per-frame: our waterfront ================= */
function drawOtters(t) {
  for (const o of OTTERS) {
    const dv = otterDive(o), end = dv + 0.7;
    if (t < o.t0 || t >= end) continue;
    const spr = OTTER_C[o.big ? 'big' : 'small'], gait = frac(t * (o.big ? 2.6 : 3.2) + o.i * 0.3) < 0.5 ? 0 : 1;
    let x = otterX(o, t), y = OTTER_Y - spr[0].height;
    if (t >= dv) { const s = (t - dv) / 0.7; x = o.dive + s * 2; y = Math.round(lerp(y, QUAY - 1, s)); }   // over the kerb, under the rail
    const X = Math.round(x);
    if (t >= dv) { ctx.save(); ctx.beginPath(); ctx.rect(0, QUAY + 2, W, 20); ctx.clip(); }
    const still = t >= OTTER_PAUSE[0] && t < OTTER_PAUSE[1];
    ctx.drawImage(spr[still ? 0 : gait], X, y);
    if (still && o.big && frac(t / 1.2) < 0.5) rect(ctx, X + 15, y, 2, 1, '#8a6a50');   // the lead one lifts its head, sniffing
    if (t >= dv) ctx.restore();
    else { ctx.globalAlpha = 0.25; ctx.drawImage(spr[gait], 0, spr[0].height - 1, spr[0].width, 1, X, OTTER_Y + 1, spr[0].width, 1); ctx.globalAlpha = 1; }   // wet sheen under them
  }
  // splashes where they slipped into the bay
  for (const o of OTTERS) {
    const s = (t - otterDive(o) - 0.6) / 1.6;
    if (s < 0 || s >= 1) continue;
    const rr = 1 + s * 5, cx = o.dive + 4, cy = QUAY - 5;
    ctx.fillStyle = '#e8e0f4'; ctx.globalAlpha = (1 - s) * 0.7;
    for (let j = 0; j < 12; j++) { const a = j / 12 * TAU; ctx.fillRect(Math.round(cx + Math.cos(a) * rr), Math.round(cy + Math.sin(a) * rr * 0.3), 1, 1); }
    if (s < 0.3) { ctx.fillRect(cx, cy - 2 - Math.round(s * 6), 1, 2); ctx.fillRect(cx - 2, cy - 1 - Math.round(s * 4), 1, 1); ctx.fillRect(cx + 2, cy - 1 - Math.round(s * 4), 1, 1); }
    ctx.globalAlpha = 1;
  }
}
/* The people are painted once per pose at build time (paint*, baked by bake()); the frame picks a pose and blits it */
function bake(x0, y0, w, h, paint) {                 // paints into a canvas of its own, the size of a box of the frame
  const [c, x] = makeCanvas(w, h), keep = ctx;
  x.translate(-x0, -y0); ctx = x; paint(); ctx = keep;
  return { c, x: x0, y: y0 };
}
const blitAt = (o, dx, dy) => ctx.drawImage(o.c, o.x + dx, o.y + dy);
function paintWalker(P, step) {                      // at the start of WALKER's walk
  const x = WALKER.x0, fy = WALKER.y0, hy = fy - P.ll - P.th + 1 - P.hh;
  wetMirror(x, fy, 7, P);
  R(x - 2, hy - 2, 1, P.hh + 2, BROLLY.walk[1]);
  person(x, fy, P, { view: 2, dir: -1, step, arm: 'brolly' });
  brolly(x - 2, hy - 6, BROLLY.walk);
}
function paintSnapper(pose) {                        // 'photo' or 'down' facing the bay, or -1 / 1 turned to film
  const { x, fy } = SNAP, P = SNAP_P, hy = fy - P.ll - P.th + 1 - P.hh, [, shaft] = BROLLY.snap;
  wetMirror(x, fy, 6, P);
  if (pose === -1 || pose === 1) {
    R(x - pose, hy - 2, 1, P.hh + 4, shaft);                                    // held in his far hand
    person(x, fy, P, { view: 2, dir: pose, arm: 'film' });
    brolly(x - pose, hy - 6, BROLLY.snap);
  } else {
    R(x - 1, hy - 2, 1, 2, shaft);
    person(x, fy, P, { view: 0, aL: 'brolly', aR: pose === 'photo' ? 'photo' : 0 });
    brolly(x - 1, hy - 6, BROLLY.snap);
  }
}
function paintPair(pose) {                           // at the stop: 'stop' facing the towers, or the stride frame (0 / 1)
  const x = STROLL.xs, fy = STROLL.fy, [M, F] = PAIR, hy = fy - M.ll - M.th + 1 - M.hh, [, shaft] = BROLLY.pair;
  if (pose === 'stop') {
    wetMirror(x, fy, 15, M);
    R(x, hy - 2, 1, M.hh + 5, shaft);
    person(x - 4, fy - 1, F, { view: 0, lean: 1 });                            // her head on his shoulder
    person(x + 4, fy, M, { view: 0, aL: 'brolly' });
    brolly(x, hy - 6, BROLLY.pair);
    return;
  }
  wetMirror(x - 2, fy, 12, M);
  person(x - 4, fy - 1, F, { view: 2, dir: -1, step: !pose });                 // the two out of step
  R(x - 2, hy - 2, 1, M.hh + 2, shaft);
  person(x, fy, M, { view: 2, dir: -1, step: !!pose, arm: 'brolly' });
  brolly(x - 2, hy - 6, BROLLY.pair);
}
function paintDiner(D, o) {
  stool(D.x, D.fy, D.stool, D.view, D.dir);
  person(D.x, D.fy, D.P, { view: D.view, dir: D.dir, sit: true, ...o });
}
function paintTable(k) {                             // a table and what stays on it (the drinks come and go per frame)
  table(...TABLES[k]);
  if (k === 0) {
    R(410, 239, 4, 1, '#ece6dc'); R(411, 238, 2, 1, '#e8a050');                 // his bowl of noodles
    R(414, 239, 4, 1, '#d8d4e0'); R(415, 238, 2, 1, '#e8582a');                 // a plate of chilli crab to share
  } else {
    R(446, 248, 6, 1, '#d8d4e0'); for (let i = 0; i < 3; i++) { R(446 + i * 2, 247, 1, 1, '#9a4a22'); R(447 + i * 2, 247, 1, 1, '#d8b070'); }   // satay
    R(453, 248, 2, 1, '#e4dee8'); R(453, 247, 2, 1, '#c87a30');                 // the peanut sauce
  }
}
function drawWalker(t) {
  // someone under a pink umbrella comes round the stall and crosses the promenade towards us, growing as they come
  if (t < WALKER.a || t >= WALKER.b) return;
  const s = (t - WALKER.a) / (WALKER.b - WALKER.a);
  blitAt(WALKER_SPR[Math.round(s * 3)][frac(t * 1.6) < 0.5 ? 1 : 0],
    Math.round(lerp(WALKER.x0, WALKER.x1, s)) - WALKER.x0, Math.round(lerp(WALKER.y0, WALKER.y1, s)) - WALKER.y0);
}
function drawSnapper(t) {
  // under his umbrella at the rail, a phone up to the skyline (lowered now and then to look at the shots, never during the
  // lasers); he turns to film the otters as they come along the rail and follows them to their dive
  const turn = t >= SNAP.otters[0] && t < SNAP.otters[2] ? (t < SNAP.otters[1] ? -1 : 1) : 0;
  blit(SNAP_SPR[turn || (frac(t / 24 + 0.3) > 0.2 || inEvent(t, [LASER]) ? 'photo' : 'down')]);
}
function drawStroll(t) {
  // a couple under one umbrella: round the stall, along the rail, a stop to look at the towers, then on behind the bench
  // and out past the rain tree (3.2 stride frames a second)
  const S = STROLL;
  if (t < S.a || t >= S.b) return;
  if (t >= S.stop && t < S.go) { blit(PAIR_SPR.stop); return; }
  const x = Math.round(t < S.stop ? lerp(S.x0, S.xs, (t - S.a) / (S.stop - S.a)) : lerp(S.xs, S.x1, (t - S.go) / (S.b - S.go)));
  blitAt(PAIR_SPR[frac(t * 1.6) < 0.5 ? 1 : 0], x - S.xs, 0);
}
function drawDiners(t) {
  // two tables in front of the stall: a man over his noodles and his friend talking across table A (she leans in, now
  // and then lifts her teh), a woman behind table B with a lime juice and a man eating satay at its near side
  const [A1, A2, B2, B1] = DINE_SPR, teh = frac(t / 30 + 0.3) < 0.08, lime = frac(t / 20 + 0.6) < 0.12;
  blit(TABLE_SPR[0]);
  blit(A1[frac(t / 5 + 0.1) < 0.26 ? 'mouth' : 'table']);
  blit(A2[teh ? 'drink' : (frac(t / 8 + 0.5) < 0.45 ? 'talk' : 'table') + (frac(t / 12 + 0.2) < 0.3 ? '1' : '')]);
  if (!teh) { R(419, 237, 1, 3, '#c08048'); R(419, 237, 1, 1, '#f0dcb8'); }
  ctx.fillStyle = '#e8e0ec';                                                    // steam off the noodles
  for (let k = 0; k < 2; k++) {
    const u = frac(t * 80 / LOOP + k * 0.5);
    ctx.globalAlpha = 0.4 * (1 - u); ctx.fillRect(411 + k + Math.round(Math.sin(TAU * (u + k * 0.3)) * 0.8), 237 - Math.round(u * 7), 1, 1);
  }
  ctx.globalAlpha = 1;
  blit(B2[lime ? 'drink' : frac(t / 7.5 + 0.2) < 0.25 ? 'mouth' : 'rest']);
  blit(TABLE_SPR[1]);
  if (!lime) { R(459, 246, 1, 3, '#8ac850'); R(459, 246, 1, 1, '#d8f8b0'); R(460, 245, 1, 1, '#ff6a8a'); }
  blit(B1[frac(t / 6 + 0.35) < 0.3 ? 'mouth' : 'table']);
}
function drawPalms(t) {
  blit(TRUNK_C);
  PALMS.forEach((pl, i) => {
    const sw = wave(t, 36 + i * 7, i * 0.3) + 0.5 * wave(t, 11, i * 0.5), k = sw > 0.45 ? 2 : sw < -0.45 ? 0 : 1;
    const top = palmTrunk(pl).at(-1);
    ctx.drawImage(CROWN_C[i][k], top[0] - 32, top[1] - 12);
  });
}
function drawLamps(t) {
  LAMPS.forEach(({ x }, i) => { const f = 0.94 + 0.06 * wave(t, 900 + i * 31); halo(HL.lamp, x + 1, 175, f); halo(HL.lampCore, x + 1, 175, f); });
  for (const { x } of LAMPS) streak(ctx, t, x + 1, 6, '#ffc878', 0.3, x, QUAY + 3, H);
  const ps = step(t, 480);                           // puddles shimmer with the bay's colours
  PUDDLES.forEach(([cx, cy, rx], k) => {
    const cols = k === 1 ? ['#ffd8a0', '#ff9ae8'] : ['#c8b0ff', '#ffc878'];
    for (let q = 0; q < 6; q++) {
      if (hash(k, q, ps) < 0.4) continue;
      ctx.globalAlpha = 0.45; ctx.fillStyle = cols[q & 1];
      ctx.fillRect(Math.round(cx - rx * 0.7 + hash(k * 9 + q, ps, 3) * rx * 1.4), cy - 1 + (q % 3), 2, 1);
    }
  });
  ctx.globalAlpha = 1;
}
function drawPuddleRings(t, level) {
  ctx.fillStyle = '#c8b0d0';
  for (const g of PUDDLE_RINGS) {
    if (hash(g.id, 3, 3) > level + 0.1) continue;
    const q = t * g.n / LOOP + g.ph, fq = Math.floor(q), cyc = fq % g.n, u = q - fq;
    if (u > 0.5) continue;
    const [cx, cy, rx, ry] = PUDDLES[g.p], s = u / 0.5, x = cx + (hash(g.id, cyc, 1) - 0.5) * rx * 1.4, y = cy + (hash(g.id, cyc, 2) - 0.5) * ry;
    const rr = 1 + s * 3, pts = 8;
    ctx.globalAlpha = (1 - s) * 0.55;
    for (let j = 0; j < pts; j++) { const a = j / pts * TAU; ctx.fillRect(Math.round(x + Math.cos(a) * rr), Math.round(y + Math.sin(a) * rr * 0.35), 1, 1); }
  }
  ctx.globalAlpha = 1;
}
function tubeLevel(t) {
  let v = 0.95 + 0.05 * wave(t, 1500, 0.3);
  if (inEvent(t, TUBE_FLICK)) { const hv = hash(11, Math.floor(t * 20)); v *= hv > 0.45 ? 1 : hv > 0.2 ? 0.45 : 0.1; }
  return v;
}
function drawStall(t) {
  halo(HL.stall, 440, 200, 0.9);
  blit(STALL_C);
  const tl = tubeLevel(t);
  rect(ctx, 420, AWN.y1 + 2, 40, 1, tl > 0.6 ? '#f4fff8' : '#6a7a74');
  halo(HL.tube, 440, AWN.y1 + 4, tl);
  // the auntie behind the glass counter wiping it down between orders
  blit(HAWKER_SPR[frac(t / 20 + 0.4) < 0.55 ? Math.round(wave(t, 80) * 2) : 'rest']);
  if (tl < 0.9) { ctx.globalAlpha = (0.9 - tl) * 0.5; rect(ctx, STALL.x0 + 2, AWN.y1 + 1, W - STALL.x0 - 2, 45, '#1a1014'); ctx.globalAlpha = 1; }
  // a man at the counter waiting for his satay, now and then looking over at the grill
  blit(QUEUER_SPR[frac(t / 16 + 0.7) < 0.4 ? 1 : 0]);
  // the sign's letters and its glow
  ctx.drawImage(SIGN_TXT_C, SIGN.x, SIGN.y);
  halo(HL.sign, SIGN.x + SIGN.w / 2, SIGN.y + 5, 0.9);
  // the uncle behind the grill (it hides him from the waist down), fanning the coals: the fan in his hand, a flap
  // every other beat
  ctx.drawImage(UNCLE_C, UNCLE.x, UNCLE.y);
  const hx = UNCLE.x + 7, hy = UNCLE.y + 6, flap = Math.round(wave(t, 160));
  rect(ctx, hx, hy, 1, 1, UNCLE_KEY.f);                                                    // his hand
  rect(ctx, hx + 1, hy, 1, 1, '#6a4a2a'); rect(ctx, hx + 2, hy + flap, 1, 1, '#6a4a2a');   // the handle
  rect(ctx, hx + 3, hy - 1 + 2 * flap, 3, 1, '#f0d488'); rect(ctx, hx + 3, hy + 2 * flap, 3, 1, '#b08a44');   // the woven blade, its underside in shade
  // the grill in front of him: coals glowing and flickering, rows of satay sticks
  const s = step(t, 2400);
  for (let x = GRILL.x; x < GRILL.x + GRILL.w; x++) {
    const h1 = hash(x, s, 13);
    ctx.fillStyle = h1 > 0.8 ? '#ffe070' : h1 > 0.45 ? '#ff7a2a' : h1 > 0.2 ? '#c83a1a' : '#5a1a10';
    ctx.fillRect(x, GRILL.y + 1, 1, 1);
  }
  ctx.fillStyle = '#7a4a24';
  for (let x = GRILL.x + 1; x < GRILL.x + GRILL.w - 1; x += 2) ctx.fillRect(x, GRILL.y, 1, 1);
  ctx.fillStyle = '#c89048';
  for (let x = GRILL.x + 1; x < GRILL.x + GRILL.w - 1; x += 4) ctx.fillRect(x, GRILL.y - 1, 1, 1);
  halo(HL.coal, GRILL.x + 12, GRILL.y + 1, 0.75 + 0.25 * hash(1, step(t, 1200), 5));
  halo(HL.coal, GRILL.x + 30, GRILL.y + 1, 0.75 + 0.25 * hash(2, step(t, 1200), 5));
  // bulbs hanging from the awning, swaying a little
  BULBS.forEach(([x, y], i) => {
    const sw = Math.round(wave(t, 34 + i * 5, i * 0.3) * 0.8);
    rect(ctx, x, AWN.y1 + 1, 1, y - AWN.y1 - 2, '#2a2230'); rect(ctx, x - 1 + sw, y - 1, 3, 2, '#fff0c0');
    halo(HL.bulb, x + sw, y, 0.9);
  });
}
function drawSmoke(t) {
  // satay smoke: puffs rise off the coals, gather under the awning, and spill out over its edge into the rain
  for (const sm of SMOKERS) for (let j = 0; j < 3; j++) {
    const u = frac(t * (30 + sm.k * 3 + j * 11) / LOOP + sm.k * 0.137 + j * 0.33);
    const rise = u * 84, y = GRILL.y - 2 - rise;
    let x = sm.x + Math.sin(TAU * (u * 1.5 + sm.k * 0.3)) * 2.5 - u * 16;
    if (y < AWN.y1 + 2) x -= (AWN.y1 + 2 - y) * 0.3;   // spreading as it spills past the eave
    const r = u < 0.2 ? 1 : u < 0.45 ? 2 : u < 0.7 ? 3 : 4;
    ctx.globalAlpha = Math.min(1, 0.9 * Math.min(1, u * 6) * Math.pow(1 - u, 0.6));
    ctx.drawImage(PUFF_C[r][u < 0.35 ? 0 : 1], Math.round(x) - r, Math.round(y) - r);
  }
  ctx.globalAlpha = 1;
}
function drawDrips(t, level) {
  const g = 420;
  ctx.fillStyle = '#f0dcf0';
  for (const d of DRIPS) {
    const T = LOOP / d.n, q = t / T + d.ph, fq = Math.floor(q), u = q - fq, tau = u * T, rel = T * 0.5;
    if (hash(d.id, fq % d.n, 21) > 0.25 + 0.75 * level) continue;   // fewer drops fall as the shower eases
    if (tau < rel) { ctx.globalAlpha = 0.2 + 0.6 * tau / rel; ctx.fillRect(d.x, d.y0, 1, 1); continue; }
    const s = tau - rel, y = d.y0 + 0.5 * g * s * s;
    if (y < d.y1 - 1) { ctx.globalAlpha = 0.85; ctx.fillRect(d.x, Math.round(y), 1, 2); continue; }
    const ss = (s - Math.sqrt(2 * (d.y1 - 1 - d.y0) / g)) / 0.3;
    if (ss >= 1) continue;
    const hy = d.y1 - 1 - Math.round(Math.sin(Math.PI * ss) * 2), sp = 1 + Math.round(ss * 2);
    ctx.globalAlpha = 1 - ss;
    ctx.fillRect(d.x - sp, hy, 1, 1); ctx.fillRect(d.x + sp, hy, 1, 1);
  }
  ctx.globalAlpha = 1;
}
function drawGlints(t) {
  const gl = 0.25 + 0.75 * env(t, 150, 238, 20), s = step(t, 960);   // most after the shower, everything still dripping
  for (let i = 0; i < GLINTS.length; i++) {
    const h = hash(i, s, 71);
    if (h > 0.1 * gl) continue;
    const [x, y] = GLINTS[i], a = 1 - h / (0.1 * gl);
    ctx.globalAlpha = a; rect(ctx, x, y, 1, 1, '#fff4ff');
    ctx.globalAlpha = a * 0.35; rect(ctx, x - 1, y, 3, 1, '#ffe8f8'); rect(ctx, x, y - 1, 1, 3, '#ffe8f8');
  }
  ctx.globalAlpha = 1;
}
function drawEaves(t, level) {
  // water running off the awning's front edge: a curtain of drops when it pours, a few when it drizzles
  const fall = 72, land = AWN.y1 + fall;
  for (const e of EAVES) {
    if (hash(e.id, 7, 7) > level * 1.1) continue;
    const q = t * e.n / LOOP + e.ph, fq = Math.floor(q), u = q - fq;
    const y = AWN.y1 + 1 + u * u * fall, lit = e.x > 404 && e.x < 470;
    ctx.fillStyle = lit ? '#ffe0b0' : '#d8c8e0'; ctx.globalAlpha = 0.55;
    if (u < 0.92) ctx.fillRect(e.x, Math.round(y), 1, 3);
    else { ctx.globalAlpha = 0.5; ctx.fillRect(e.x - 1, land - 1, 1, 1); ctx.fillRect(e.x + 1, land - 1, 1, 1); }
  }
  ctx.globalAlpha = 1;
}
function drawRain(L, t, board, level) {
  const n = Math.round(L.drops.length * level);
  for (let i = 0; i < n; i++) {
    const d = L.drops[i], q = t * d.n / LOOP + d.ph, fq = Math.floor(q), cyc = fq % d.n, u = q - fq;
    const xl = -12 + hash(d.id, cyc, 11) * (W + 24 + L.fall * L.slope * 0.5);
    const yl = L.land(Math.round(xl), hash(d.id, cyc, 12), hash(d.id, cyc, 13));
    if (u < L.ff) {
      const s = u / L.ff, yh = yl - L.fall * (1 - s), xh = xl - (yl - yh) * L.slope;
      if (yh < -1) continue;
      const li = litAt(xh, yh, board);
      ctx.fillStyle = li < 0 ? L.col : L.lit[li];
      for (let k = 0; k < L.len; k++) ctx.fillRect(Math.round(xh - k * L.slope), Math.round(yh - k), 1, 1);
    } else {
      const s = (u - L.ff) / (1 - L.ff), big = L.len > 6;
      ctx.globalAlpha = (1 - s) * (L.water ? 0.6 : 1);
      ctx.fillStyle = L.splash;
      if (L.water) {
        const rr = 1 + s * 2.5;
        ctx.fillRect(Math.round(xl - rr), Math.round(yl), 1, 1); ctx.fillRect(Math.round(xl + rr), Math.round(yl), 1, 1);
      } else {
        const hgt = Math.sin(Math.PI * s) * (big ? 3 : 1.5), spread = 1 + s * (big ? 3 : 1.5), Y = Math.round(yl - 1 - hgt);
        ctx.fillRect(Math.round(xl - spread), Y, 1, 1); ctx.fillRect(Math.round(xl + spread), Y, 1, 1);
        if (s < 0.35) ctx.fillRect(Math.round(xl), Math.round(yl - 1), 1, 1);
      }
      ctx.globalAlpha = 1;
    }
  }
}

/* ================= Frame compositor (back to front) ================= */
// pitch: a billboard face over the ad; billboard: false leaves the sign out (the loop videos); weather: a WEATHERS pick
// (site/js/engine/weather.js), the city's own when left out. The far lightning is the city's own storm only: a picked
// weather drops its thunder too (weatherScene), and reduced motion leaves it out
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over'; ctx.imageSmoothingEnabled = false;
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX, fl = wx === WEATHER('city') && !reducedMotion ? flashAt(t) : null;
  const pour = pourLevel(t), level = rainLevel(t), lv = laserLevel(t);
  drawSky(t, fl, pour);
  drawLasers(t, lv);
  drawLayer(FAR, t, 5);
  drawFlyer(t);
  drawCbd(t);
  drawGardens(t);
  if (wx.rain) drawRain(RAIN[0], t, billboard, level);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawMbs(t);
  drawFog(FOGS[0], t);
  drawArt(t);
  drawHelix(t);
  drawBlinkers(t);
  if (billboard) drawBillboard(t, pitch);
  if (wx.rain && pour > 0.01) { ctx.globalAlpha = 0.34 * pour; drawScroll(VEIL_C, 20, 3, t); ctx.globalAlpha = 1; }
  drawFog(FOGS[1], t);
  drawWater(t, billboard, wx.rain, level, lv);
  drawBoat(BOATS[0], t);
  drawBoat(BOATS[1], t);
  if (wx.rain) drawRain(RAIN[1], t, billboard, level);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  blit(FRONT_C);
  drawLamps(t);
  if (wx.rain) { drawPuddleRings(t, level); drawGroundPits(t, level); }
  drawPalms(t);
  drawSnapper(t);                                    // the promenade back to front: the rail (feet on row 224), the otters (231),
  drawOtters(t);                                     // the couple (233), the bench (247), the walker (252), the planted bed
  drawStroll(t);
  blit(BENCH_C);
  drawWalker(t);
  blit(BED_C);
  drawStall(t);
  drawSmoke(t);
  blit(CANOPY.c);
  if (wx.rain) { drawGlints(t); drawDrips(t, level); drawEaves(t, level); }
  drawDiners(t);                                     // in front of the awning's curtain of drips
  if (wx.rain) drawRain(RAIN[2], t, billboard, level);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'singapore',
  name: 'Singapore',
  tagline: 'Monsoon night on Marina Bay',
  timeZone: 'Asia/Singapore',
  accent: '#ff85f3',                        // = ACCENT, written out: tools/seo/seo.mjs reads it from the source
  focusX: FOCUS,
  landmark: [MBS.x0, MBS.x1],                          // Marina Bay Sands, cantilever tip to the south tower
  landmarkTop: DECK - 6,                               // the row of the SkyPark masts' tip lights
  billboard: BB,
  ambience: { rain: 0.65, water: 0.3, crowd: 0.25, traffic: 0.15 },
  events: [
    { t: BOATS[0].a + 1, type: 'horn', x: 20, gain: 0.35 },
    { t: BOATS[0].a + 1.5, type: 'boat', dur: 86, pan: [-0.9, 0.9], gain: 0.35 },
    ...OTTERS.filter(o => o.i === 0 || o.i === 4).map(o => ({ t: +(otterDive(o) + 0.6).toFixed(1), type: 'splash', x: o.dive, gain: 0.3 })),
    ...FLASHES.map(f => ({ t: +(f.t + 1.6).toFixed(1), type: 'thunder', x: f.x, gain: f.big ? 0.6 : 0.4 })),
    { t: BOATS[1].a + 1, type: 'horn', x: 460, gain: 0.35 },
    { t: BOATS[1].a + 1.5, type: 'boat', dur: 84, pan: [0.9, -0.9], gain: 0.35 },
    { t: LASER[0], type: 'chime', x: 290 },
  ],
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
